import React, { useState, useCallback, useRef, useEffect } from "react";

// ─── CONFIG ───────────────────────────────────────────────────────────────────
const SHEET_ID       = process.env.REACT_APP_SHEET_ID;
const SHEETS_API_KEY = process.env.REACT_APP_SHEETS_API_KEY;
const SCRIPT_URL     = process.env.REACT_APP_SCRIPT_URL;
const DIRECTOR_EMAIL = "chorn@betterdebtsolutions.com";
const DIRECTOR_EMAILS = ["chorn@betterdebtsolutions.com", "arash@betterdebtsolutions.com", "jalvarez@betterdebtsolutions.com"];

const RANGES = [
  "MANAGER_TASKS!A1:O2000",
  "WEEKLY_MANAGER_TASKS!A1:M2000",
  "MTD_MANAGER_TASKS!A1:M2000",
  "DAILY_MANAGER_PREP_TASKS!A1:O500",
  "TEAM_MASTER!A1:D100",
  "DIRECTOR_BROADCAST!A1:E500",
  "CALL_REVIEWS!A1:K1000",
  "CALL_REVIEW_TASKS!A1:L2000",
];

// ─── HELPERS ──────────────────────────────────────────────────────────────────
function Bdg({ children, cls }) {
  return <span className={`inline-flex items-center text-xs font-bold px-2 py-0.5 rounded border ${cls}`}>{children}</span>;
}
function priorityBadge(p) {
  if (p === "Critical") return "bg-red-500/20 text-red-400 border-red-500/40";
  if (p === "High")     return "bg-amber-500/20 text-amber-400 border-amber-500/40";
  return "bg-cyan-500/20 text-cyan-400 border-cyan-500/40";
}
function statusBadge(s) {
  if (s === "Completed")      return "bg-green-500/20 text-green-400 border-green-500/40";
  if (s === "Pending Review") return "bg-purple-500/20 text-purple-400 border-purple-500/40";
  return "bg-blue-500/20 text-blue-400 border-blue-500/40";
}
function proofBadge(p) {
  if (p === "Submitted") return "bg-green-500/20 text-green-400 border-green-500/40";
  return "bg-red-500/20 text-red-400 border-red-500/40";
}
const TYPE_LABELS = {
  "DONUT_CHAT_GROUP":"🍩 Donut Chat","ZERO_CONVERSION":"🚫 0% Conversion (Same Day)",
  "LOW_CONVERSION":"📉 Low Conversion","STREAK_2":"🔴 2 Days No Deal",
  "STREAK_3":"🔴 3 Days No Deal","STREAK_4":"🔴 4 Days No Deal","STREAK_5":"🔴 5 Days No Deal",
  "FREQ_2_OF_3":"🎯 2 Donuts in 3 Days","FREQ_3_OF_5":"🎯 3 Donuts in 5 Days",
  "SATURDAY_SIGNUPS_REVIEW":"📋 Saturday Sign-Ups","MORNING_MESSAGE_PREP":"☀️ Morning Prep",
  "WEEKLY_DOUBLE_MISS":"⛔ Weekly Double Miss","WEEKLY_LOW_PRODUCTION":"📊 Weekly Low Production",
  "WEEKLY_LOW_CONVERSION":"📊 Weekly Low Conversion","MTD_TRIPLE_MISS":"🚨 MTD Triple Miss",
  "MTD_PRODUCTION_CONVERSION":"⚠️ MTD Prod + Conv","MTD_PRODUCTION_CANCEL":"⚠️ MTD Prod + Cancel",
  "MTD_CONVERSION_CANCEL":"⚠️ MTD Conv + Cancel","MTD_LOW_PRODUCTION":"📊 MTD Low Production",
  "MTD_LOW_CONVERSION":"📊 MTD Low Conversion","MTD_HIGH_CANCEL":"❌ MTD High Cancel",
  "GOALS_EMAIL":"📧 Goals Email (IN PERSON + EMAIL)",
  "DAILY_SPOT_CHECK":"🔍 Daily Compliance Check",
  "COMPLIANCE_CHECK_1":"🔍 Compliance Check #1",
  "COMPLIANCE_CHECK_2":"🔍 Compliance Check #2",
  "COMPLIANCE_CHECK_3":"🔍 Compliance Check #3",
  "COMPLIANCE_CHECK_4":"🔍 Compliance Check #4",
  "WEEKLY_GOALS_UPDATE":"📊 Weekly Goals Update",
  "MID_MONTH_UPDATE":"📅 Mid-Month Update",
  "CALL_REVIEW":"📞 Call Review",
  "ONE_ON_ONE_1":"🤝 1-on-1 Session #1",
  "ONE_ON_ONE_2":"🤝 1-on-1 Session #2",
  "AGENT_TIME_DETAIL":"🕐 Agent Time Detail",
  "FOLLOWUP_CALLS":"📞 Next Day Follow Ups",
};
function friendlyType(t) { return TYPE_LABELS[t] || t; }
function rowsToObjects(rows) {
  if (!rows || rows.length < 2) return [];
  const headers = rows[0].map(h => h.trim());
  return rows.slice(1).map((row, idx) => {
    const obj = { _rowIndex: idx + 2 };
    headers.forEach((h, i) => { obj[h] = row[i] || ""; });
    return obj;
  });
}

// ─── API CALLS ────────────────────────────────────────────────────────────────
async function fetchSheetData() {
  const rangeList = RANGES.map(r => `ranges=${encodeURIComponent(r)}`).join("&");
  const res = await fetch(
    `https://sheets.googleapis.com/v4/spreadsheets/${SHEET_ID}/values:batchGet?${rangeList}&key=${SHEETS_API_KEY}`
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(`Sheets API ${res.status}: ${err?.error?.message || "unknown"}`);
  }
  const json = await res.json();
  const [mRows, wRows, mtdRows, prepRows, teamRows, broadcastRows, crRows, crTaskRows] = json.valueRanges.map(r => r.values || []);
  return {
    managerTasks:     rowsToObjects(mRows),
    weeklyTasks:      rowsToObjects(wRows),
    mtdTasks:         rowsToObjects(mtdRows),
    prepTasks:        rowsToObjects(prepRows),
    teamMaster:       rowsToObjects(teamRows),
    broadcast:        rowsToObjects(broadcastRows),
    callReviews:      rowsToObjects(crRows),
    callReviewTasks:  rowsToObjects(crTaskRows),
  };
}

async function updateTask(sheetName, rowIndex, updates) {
  const res = await fetch(SCRIPT_URL, {
    method: "POST",
    headers: { "Content-Type": "text/plain" },
    body: JSON.stringify({ sheetName, rowIndex, ...updates }),
  });
  return await res.json().catch(() => ({ success: false }));
}

async function postBroadcast(message, postedBy, replyTo, authorType) {
  const res = await fetch(SCRIPT_URL, {
    method: "POST",
    headers: { "Content-Type": "text/plain" },
    body: JSON.stringify({ action: "postBroadcast", message, postedBy, replyTo: replyTo||"", authorType: authorType||"director" }),
  });
  return await res.json().catch(() => ({ success: false }));
}

async function saveGoalsTracker(updates) {
  const res = await fetch(SCRIPT_URL, {
    method: "POST",
    headers: { "Content-Type": "text/plain" },
    body: JSON.stringify({ action: "saveGoalsTracker", updates }),
  });
  return await res.json().catch(() => ({ success: false }));
}

async function saveYesterdayStats(teamStats) {
  const res = await fetch(SCRIPT_URL, {
    method: "POST",
    headers: { "Content-Type": "text/plain" },
    body: JSON.stringify({ action: "saveYesterdayStats", teamStats }),
  });
  return await res.json().catch(() => ({ success: false }));
}

async function pushTask(taskType, action, managers) {
  const results = await Promise.all(managers.map(mgr =>
    fetch(SCRIPT_URL, {
      method: "POST",
      headers: { "Content-Type": "text/plain" },
      body: JSON.stringify({ action: "pushTask", taskType, taskAction: action, managerName: mgr.name, managerTeam: mgr.team }),
    }).then(r => r.json()).catch(() => ({ success: false }))
  ));
  return results.every(r => r.success);
}

async function uploadProofImage(base64Data, userName) {
  try {
    const fileName = `proof_${userName}_${Date.now()}.png`;
    const sizeKB = Math.round(base64Data.length * 0.75 / 1024);
    console.log("Uploading proof image, size:", sizeKB + "KB");
    const res = await fetch(SCRIPT_URL, {
      method: "POST",
      headers: { "Content-Type": "text/plain" },
      body: JSON.stringify({ action: "uploadImage", base64Data, fileName }),
    });
    console.log("Upload response status:", res.status);
    const text = await res.text();
    console.log("Upload response body:", text);
    const result = JSON.parse(text);
    console.log("Upload result:", result);
    return result.url || null;
  } catch(e) {
    console.error("Upload failed:", e);
    return null;
  }
}

// ─── LOGIN SCREEN ─────────────────────────────────────────────────────────────
function LoginScreen({ onLogin }) {
  const [email,   setEmail]   = useState("");
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");

  const handleLogin = useCallback(async () => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) { setError("Please enter your email address."); return; }
    setLoading(true);
    setError("");
    try {
      // Allow director email without checking TEAM_MASTER
      if (DIRECTOR_EMAILS.includes(trimmed)) {
        onLogin({ email: trimmed, name: "Chris Horn", avatar: null });
        return;
      }
      const res = await fetch(
        `https://sheets.googleapis.com/v4/spreadsheets/${SHEET_ID}/values/TEAM_MASTER!A1:D100?key=${SHEETS_API_KEY}`
      );
      const json = await res.json();
      const rows = json.values || [];
      const headers = rows[0] || [];
      const emailCol = headers.findIndex(h => h.toLowerCase().includes("email"));
      const nameCol  = headers.findIndex(h => h.toLowerCase() === "manager");
      const match = rows.slice(1).find(row => (row[emailCol] || "").toLowerCase() === trimmed);
      if (!match) {
        setError("Email not found. Please use your work email address.");
        setLoading(false);
        return;
      }
      const name = match[nameCol] || trimmed.split("@")[0];
      onLogin({ email: trimmed, name, avatar: null });
    } catch(e) {
      setError("Login failed. Please try again.");
      setLoading(false);
    }
  }, [email, onLogin]);

  const handleKeyDown = (e) => { if (e.key === "Enter") handleLogin(); };

  return (
    <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:"#0f1520", fontFamily:"'Inter',system-ui,sans-serif" }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <div style={{ width:380, display:"flex", flexDirection:"column", alignItems:"center", gap:32 }}>
        <div style={{ textAlign:"center" }}>
          <div style={{ fontSize:36, fontWeight:900, letterSpacing:"0.3em", color:"#fff" }}>RO<span style={{ color:"#00d4ff" }}>QOS</span></div>
          <div style={{ fontSize:12, letterSpacing:"0.2em", textTransform:"uppercase", color:"#64748b", marginTop:4 }}>Sales Floor OS</div>
        </div>
        <div style={{ width:"100%", background:"#1e2840", border:"1px solid #2a3148", borderRadius:16, padding:32, display:"flex", flexDirection:"column", gap:16 }}>
          <div style={{ textAlign:"center" }}>
            <div style={{ color:"#fff", fontWeight:600, fontSize:18, marginBottom:4 }}>Welcome back</div>
            <div style={{ color:"#64748b", fontSize:13 }}>Enter your work email to continue</div>
          </div>
          {error && <div style={{ background:"#1a0808", border:"1px solid #3a1a1a", borderRadius:8, padding:"8px 12px", color:"#f87171", fontSize:13, textAlign:"center" }}>{error}</div>}
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="you@betterdebtsolutions.com"
            autoFocus
            style={{ width:"100%", background:"#151c2e", border:"1px solid #2a3148", borderRadius:10, padding:"12px 14px", color:"#e2e8f0", fontSize:14, outline:"none", boxSizing:"border-box" }}
          />
          <button onClick={handleLogin} disabled={loading} style={{ width:"100%", display:"flex", alignItems:"center", justifyContent:"center", gap:12, background:loading?"#1a2035":"#00d4ff", color:"#000", border:"none", borderRadius:12, padding:"12px 16px", fontWeight:700, fontSize:14, cursor:loading?"not-allowed":"pointer", opacity:loading?0.7:1 }}>
            {loading
              ? <div style={{ display:"flex", alignItems:"center", gap:8 }}><div style={{ width:16, height:16, border:"2px solid #64748b", borderTopColor:"transparent", borderRadius:"50%", animation:"spin 0.8s linear infinite" }}/><span style={{ color:"#64748b" }}>Signing in...</span></div>
              : "Sign In →"
            }
          </button>
          <div style={{ color:"#475569", fontSize:12, textAlign:"center" }}>Access is restricted to authorized team members only</div>
        </div>
      </div>
    </div>
  );
}


// ─── DEAL TRACKER ─────────────────────────────────────────────────────────────
function DealTracker({ managerEmail, teamName }) {
  const storageKey = React.useMemo(
    () => `dealTracker:${(managerEmail || "default").toLowerCase()}`,
    [managerEmail]
  );

  const [agents,  setAgents]  = useState([]);
  const [newName, setNewName] = useState("");
  const [loaded,  setLoaded]  = useState(false);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    const SHEET_ID = process.env.REACT_APP_SHEET_ID;
    const API_KEY = process.env.REACT_APP_SHEETS_API_KEY;
    const email = (managerEmail || "").toLowerCase();
    if (!SHEET_ID || !API_KEY || !email) { setLoaded(true); return; }

    fetch(`https://sheets.googleapis.com/v4/spreadsheets/${SHEET_ID}/values/DEAL_TRACKER!A1:F2000?key=${API_KEY}`)
      .then(r => r.json())
      .then(json => {
        const rows = json.values || [];
        if (rows.length < 2) { setLoaded(true); return; }
        // rows[0] = headers, rows[1+] = data
        // Cols: Manager_Email, Team, Agent_Name, Deals, Is_Out, Last_Updated
        const agentRows = rows.slice(1).filter(r => (r[0]||"").toLowerCase() === email);
        if (agentRows.length > 0) {
          // Merge Sheet roster with localStorage — preserve deals/isOut AND any manually added agents
          try {
            const raw = localStorage.getItem(storageKey);
            const saved = raw ? JSON.parse(raw) : [];
            const dealMap = {};
            saved.forEach(a => { dealMap[a.name.toLowerCase()] = { deals: a.deals, isOut: a.isOut }; });
            // Build the sheet-based agents
            const sheetNames = new Set();
            const sheetAgents = agentRows.map(r => {
              const name = r[2] || "";
              sheetNames.add(name.toLowerCase());
              const existing = dealMap[name.toLowerCase()];
              return { name, deals: existing?.deals ?? 0, isOut: existing?.isOut ?? false };
            });
            // Keep any locally added agents NOT in the sheet
            const localOnly = saved.filter(a => !sheetNames.has(a.name.toLowerCase()));
            setAgents([...sheetAgents, ...localOnly]);
          } catch(e) {
            setAgents(agentRows.map(r => ({ name: r[2]||"", deals: 0, isOut: false })));
          }
        } else {
          // Fall back to localStorage
          try {
            const raw = localStorage.getItem(storageKey);
            if (raw) { const parsed = JSON.parse(raw); if (Array.isArray(parsed)) setAgents(parsed); }
          } catch(e) {}
        }
        setLoaded(true);
      })
      .catch(() => {
        try {
          const raw = localStorage.getItem(storageKey);
          if (raw) { const parsed = JSON.parse(raw); if (Array.isArray(parsed)) setAgents(parsed); }
        } catch(e) {}
        setLoaded(true);
      });
  }, [storageKey, managerEmail]);

  useEffect(() => {
    if (!loaded) return;
    try { localStorage.setItem(storageKey, JSON.stringify(agents)); } catch(e) {}
  }, [agents, loaded, storageKey]);

  const addAgent = () => {
    const trimmed = newName.trim();
    if (!trimmed) return;
    setAgents(prev => {
      if (prev.some(a => a.name.toLowerCase() === trimmed.toLowerCase())) return prev;
      return [...prev, { name: trimmed, deals: 0, isOut: false }];
    });
    setNewName("");
  };

  const updateDeals = (name, raw) => {
    const n = parseInt(raw, 10);
    const safe = Number.isFinite(n) && n >= 0 ? n : 0;
    setAgents(prev => prev.map(a => a.name === name ? { ...a, deals: safe } : a));
  };

  const toggleOut = (name) => {
    setAgents(prev => prev.map(a => a.name === name ? { ...a, isOut: !a.isOut } : a));
  };

  const removeAgent = (name) => {
    if (!window.confirm(`Remove ${name} from the roster?`)) return;
    setAgents(prev => prev.filter(a => a.name !== name));
  };

  const resetDay = () => {
    if (!window.confirm("Reset all deal counts to 0? Roster stays the same.")) return;
    setAgents(prev => prev.map(a => ({ ...a, deals: 0 })));
  };

  const loadRoster = async () => {
    if (!managerEmail) return;
    try {
      const res = await fetch(window.SCRIPT_URL || process.env.REACT_APP_SCRIPT_URL, {
        method: "POST",
        headers: { "Content-Type": "text/plain" },
        body: JSON.stringify({ action: "getTeamRoster", managerEmail })
      });
      const json = await res.json();
      if (json.success && Array.isArray(json.agents)) {
        setAgents(prev => {
          const existing = new Set(prev.map(a => a.name.toLowerCase()));
          const newAgents = json.agents
            .filter(name => !existing.has(name.toLowerCase()))
            .map(name => ({ name, deals: 0, isOut: false }));
          return [...prev, ...newAgents];
        });
      }
    } catch(e) {}
  };

  const leaderboard = React.useMemo(() => {
    return agents.slice().sort((a, b) => {
      if (a.isOut !== b.isOut) return a.isOut ? 1 : -1;
      if (a.deals !== b.deals) return b.deals - a.deals;
      return a.name.localeCompare(b.name);
    });
  }, [agents]);

  const totals = React.useMemo(() => {
    const active = agents.filter(a => !a.isOut);
    const totalDeals = active.reduce((s, a) => s + (Number(a.deals) || 0), 0);
    return {
      totalDeals,
      activeAgents: active.length,
      donuts: active.filter(a => (Number(a.deals) || 0) === 0).length,
      dpd: active.length > 0 ? (totalDeals / active.length) : null,
    };
  }, [agents]);

  const title = teamName ? `${teamName} Deal Tracker` : "Deal Tracker";
  const activeAgentsList = leaderboard.filter(x => !x.isOut);
  const dpdDisplay = totals.dpd === null ? "—" : totals.dpd.toFixed(1);
  const dpdColor = totals.dpd === null ? "#64748b" : totals.dpd >= 1.5 ? "#34d399" : totals.dpd >= 1.0 ? "#fbbf24" : "#f87171";

  return (
    <div style={{ background:"linear-gradient(135deg, #1e2840 0%, #1a2236 100%)", border:"1px solid #2a3148", borderRadius:16, padding:20, display:"flex", flexDirection:"column", gap:14, boxSizing:"border-box", boxShadow:"0 4px 20px rgba(0,0,0,0.2)" }}>
      {teamName && (
        <img src={`/team-logos/${teamName}.png`} alt={`${teamName} banner`}
          onError={e=>{ e.target.style.display="none"; }}
          style={{ width:"100%", borderRadius:8, maxHeight:110, objectFit:"cover", display:"block" }}/>
      )}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:8 }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <span style={{ fontSize:20 }}>🏆</span>
          <div style={{ fontSize:14, fontWeight:800, textTransform:"uppercase", letterSpacing:"0.1em", background:"linear-gradient(90deg, #34d399, #00d4ff)", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent", backgroundClip:"text" }}>{title}</div>
        </div>
        <div style={{ display:"flex", gap:6 }}>
          <button onClick={() => setEditing(v => !v)} style={{ background:editing?"#00d4ff":"transparent", color:editing?"#000":"#94a3b8", border:`1px solid ${editing?"#00d4ff":"#2a3148"}`, borderRadius:6, padding:"5px 12px", fontSize:11, fontWeight:700, cursor:"pointer" }}>
            {editing ? "✓ Done" : "✎ Edit"}
          </button>
          {editing && <button onClick={resetDay} style={{ background:"#475569", color:"#fff", border:"none", borderRadius:6, padding:"5px 12px", fontSize:11, fontWeight:700, cursor:"pointer" }}>Reset Day</button>}
          {editing && <button onClick={loadRoster} style={{ background:"#1e3a5f", color:"#00d4ff", border:"1px solid rgba(0,212,255,0.3)", borderRadius:6, padding:"5px 12px", fontSize:11, fontWeight:700, cursor:"pointer" }}>⬇ Load Roster</button>}
        </div>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"repeat(4, 1fr)", gap:8 }}>
        {[
          { label:"Deals",  value:totals.totalDeals,   color:"#34d399" },
          { label:"Active", value:totals.activeAgents, color:"#00d4ff" },
          { label:"Donuts", value:totals.donuts,       color:totals.donuts>0?"#f87171":"#64748b" },
          { label:"DPD",    value:dpdDisplay,          color:dpdColor },
        ].map(s => (
          <div key={s.label} style={{ background:"rgba(15,21,32,0.6)", borderRadius:10, padding:"10px 8px", textAlign:"center", border:"1px solid rgba(42,49,72,0.5)" }}>
            <div style={{ fontSize:22, fontWeight:900, color:s.color, lineHeight:1.1 }}>{s.value}</div>
            <div style={{ fontSize:9, fontWeight:700, textTransform:"uppercase", letterSpacing:0.8, color:"#64748b", marginTop:3 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {editing && (
        <div style={{ display:"flex", gap:8 }}>
          <input type="text" placeholder="Add agent name…" value={newName} onChange={e => setNewName(e.target.value)} onKeyDown={e => { if (e.key === "Enter") addAgent(); }}
            style={{ flex:1, background:"#151c2e", border:"1px solid #2a3148", borderRadius:8, padding:"8px 12px", color:"#e2e8f0", fontSize:13, outline:"none" }}/>
          <button onClick={addAgent} disabled={!newName.trim()} style={{ background:newName.trim()?"#00d4ff":"#1a2035", color:"#000", border:"none", borderRadius:8, padding:"8px 16px", fontWeight:700, fontSize:13, cursor:newName.trim()?"pointer":"not-allowed", opacity:newName.trim()?1:0.5 }}>Add</button>
        </div>
      )}

      {/* Leaderboard — two-column for large rosters, no inner scroll for screenshots */}
      {(() => {
        const twoCol = !editing && leaderboard.length >= 13;
        const half = Math.ceil(leaderboard.length / 2);
        const colLeft  = twoCol ? leaderboard.slice(0, half) : leaderboard;
        const colRight = twoCol ? leaderboard.slice(half)    : [];

        const renderAgentRow = (a) => {
          const rank = a.isOut ? null : activeAgentsList.indexOf(a) + 1;
          const rankBg = rank === 1 ? "linear-gradient(135deg, #fbbf24, #f59e0b)"
            : rank === 2 ? "linear-gradient(135deg, #cbd5e1, #94a3b8)"
            : rank === 3 ? "linear-gradient(135deg, #fb923c, #ea580c)"
            : "rgba(100,116,139,0.15)";
          const isTop = rank === 1;
          const cols = editing ? "22px 1fr auto auto auto" : "22px 1fr auto";
          return (
            <div key={a.name} style={{
              display:"grid", gridTemplateColumns:cols, alignItems:"center", gap:6,
              padding:"3px 4px", borderBottom:"1px solid rgba(42,49,72,0.3)",
              opacity:a.isOut?0.5:1,
              background:isTop?"rgba(251,191,36,0.06)":"transparent",
              borderRadius:isTop?4:0
            }}>
              <div style={{
                width:22, height:22, borderRadius:"50%",
                background:a.isOut?"rgba(100,116,139,0.2)":rankBg,
                display:"flex", alignItems:"center", justifyContent:"center",
                fontWeight:900, fontSize:10,
                color:a.isOut?"#64748b":rank&&rank<=3?"#000":"#64748b",
                boxShadow:rank&&rank<=3&&!a.isOut?"0 2px 6px rgba(0,0,0,0.3)":"none",
                flexShrink:0
              }}>
                {a.isOut ? "—" : rank}
              </div>
              <div style={{
                fontSize:12, fontWeight:isTop?800:500,
                color:a.isOut?"#64748b":isTop?"#fbbf24":"#e2e8f0",
                textAlign:"left", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"
              }}>
                {a.name}
                {a.isOut && <span style={{ fontSize:8, color:"#a78bfa", fontWeight:700, marginLeft:5, padding:"1px 4px", background:"rgba(167,139,250,0.15)", borderRadius:3 }}>OUT</span>}
              </div>
              <input type="number" min="0" step="1" value={a.isOut?"":a.deals} disabled={a.isOut}
                onChange={e => updateDeals(a.name, e.target.value)}
                style={{
                  width:38, padding:"3px 4px",
                  background:a.isOut?"transparent":"#151c2e",
                  border:`1px solid ${a.isOut?"transparent":"#2a3148"}`,
                  borderRadius:5,
                  color:a.isOut?"#64748b":a.deals>=2?"#34d399":a.deals===1?"#fbbf24":"#e2e8f0",
                  fontSize:13, fontWeight:900, textAlign:"center", outline:"none", boxSizing:"border-box"
                }}/>
              {editing && (
                <label style={{ display:"flex", alignItems:"center", gap:3, fontSize:10, color:"#94a3b8", cursor:"pointer", userSelect:"none" }}>
                  <input type="checkbox" checked={a.isOut} onChange={() => toggleOut(a.name)} style={{ accentColor:"#a78bfa", cursor:"pointer" }}/>
                  <span>Out</span>
                </label>
              )}
              {editing && (
                <button onClick={() => removeAgent(a.name)}
                  style={{ background:"transparent", color:"#64748b", border:"none", fontSize:16, lineHeight:1, cursor:"pointer", padding:"0 3px", width:20, height:20 }}>×</button>
              )}
            </div>
          );
        };

        if (!loaded) return <div style={{ padding:20, textAlign:"center", color:"#64748b", fontSize:13 }}>Loading…</div>;
        if (leaderboard.length === 0) return (
          <div style={{ padding:30, textAlign:"center", color:"#475569", fontSize:13 }}>
            {editing ? "Add your first agent above." : "No agents yet. Click ✎ Edit to build your roster."}
          </div>
        );
        if (twoCol) return (
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:0, borderTop:"1px solid rgba(42,49,72,0.4)" }}>
            <div style={{ borderRight:"1px solid rgba(42,49,72,0.4)", paddingRight:8 }}>{colLeft.map(renderAgentRow)}</div>
            <div style={{ paddingLeft:8 }}>{colRight.map(renderAgentRow)}</div>
          </div>
        );
        return <div>{leaderboard.map(renderAgentRow)}</div>;
      })()}
    </div>
  );
}

// ─── SPOT CHECK FORM ─────────────────────────────────────────────────────────
function SpotCheckForm({ notes, setNotes, ooo, setOoo }) {
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
      <div style={{ fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.08em", color:"#00d4ff", marginBottom:4 }}>🔍 Daily Compliance Check</div>
      <div style={{ background:"rgba(239,68,68,0.08)", border:"1px solid rgba(239,68,68,0.2)", borderRadius:8, padding:"10px 14px", display:"flex", alignItems:"center", gap:10, cursor:"pointer" }}
        onClick={()=>setOoo(v=>!v)}>
        <div style={{ width:18, height:18, borderRadius:4, border:`2px solid ${ooo?"#ef4444":"#2a3148"}`, background:ooo?"#ef4444":"transparent", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, transition:"all 0.15s" }}>
          {ooo && <span style={{ color:"#fff", fontSize:12, fontWeight:900 }}>✓</span>}
        </div>
        <div>
          <div style={{ fontSize:12, fontWeight:700, color:"#f87171" }}>Agent Out of Office</div>
          <div style={{ fontSize:11, color:"#64748b" }}>Check this if the agent was not present today</div>
        </div>
      </div>
      {!ooo && (
        <div>
          <div style={{ fontSize:11, fontWeight:600, color:"#64748b", marginBottom:6 }}>Spot Check Notes <span style={{ color:"#ef4444" }}>*required</span></div>
          <textarea value={notes} onChange={e=>setNotes(e.target.value)}
            placeholder="Log what you heard on the call, compliance notes, feedback given to agent..."
            style={{ width:"100%", background:"#1e2840", border:"1px solid #2a3148", borderRadius:8, padding:"10px 12px", color:"#e2e8f0", fontSize:13, resize:"vertical", minHeight:100, outline:"none", fontFamily:"inherit", boxSizing:"border-box" }}/>
        </div>
      )}
      {ooo && (
        <div style={{ fontSize:12, color:"#64748b", fontStyle:"italic" }}>No spot check required — agent marked out of office.</div>
      )}
    </div>
  );
}

// ─── FORM DISPLAY HELPERS ────────────────────────────────────────────────────
function SubmittedCallReview(completionNotes) {
  let d = null;
  try { d = JSON.parse(completionNotes || "{}"); } catch(e) {}
  if (!d || d.type !== "CALL_REVIEW") return null;
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
      <div style={{ fontSize:11, fontWeight:700, textTransform:"uppercase", color:"#a78bfa", marginBottom:4 }}>📞 Submitted Call Review</div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
        {[{label:"File Number(s)",value:d.fileNumbers},{label:"Date of Call",value:d.dateOfCall}].map(f=>(
          <div key={f.label} style={{ background:"#151c2e", borderRadius:8, padding:"8px 12px" }}>
            <div style={{ fontSize:10, fontWeight:700, color:"#475569", marginBottom:3 }}>{f.label}</div>
            <div style={{ fontSize:13, color:"#e2e8f0" }}>{f.value||"—"}</div>
          </div>
        ))}
      </div>
      {[{label:"✅ Strengths",value:d.strengths,color:"#34d399"},{label:"📉 Areas of Improvement",value:d.areasOfImprovement,color:"#fbbf24"},{label:"📋 Plan of Action",value:d.planOfAction,color:"#a78bfa"}].map(f=>(
        <div key={f.label} style={{ background:"#151c2e", borderRadius:8, padding:"8px 12px" }}>
          <div style={{ fontSize:10, fontWeight:700, color:f.color, marginBottom:3 }}>{f.label}</div>
          <div style={{ fontSize:13, color:"#e2e8f0", whiteSpace:"pre-wrap" }}>{f.value||"—"}</div>
        </div>
      ))}
    </div>
  );
}

function SubmittedOneOnOne(completionNotes) {
  let d = null;
  try { d = JSON.parse(completionNotes || "{}"); } catch(e) {}
  if (!d) return null;
  return (
    <div style={{ background:"#151c2e", borderRadius:8, padding:"10px 14px" }}>
      <div style={{ fontSize:10, fontWeight:700, color:"#a78bfa", marginBottom:6 }}>🤝 Session Notes</div>
      <div style={{ fontSize:13, color:"#e2e8f0", whiteSpace:"pre-wrap" }}>{d.notes||"—"}</div>
    </div>
  );
}

// ─── TASK ROW (expandable) ────────────────────────────────────────────────────
function TaskRow({ task, typeCol, sheetName, userEmail, userName, onUpdated, isDirector, checkboxCell, sourceLabel, autoExpand, onAutoExpanded }) {
  const [expanded,    setExpanded]    = useState(false);
  const [notes,       setNotes]       = useState(task.Completion_Notes || "");
  const [proofImg,    setProofImg]    = useState("");
  const [submitting,  setSubmitting]  = useState(false);
  const [localStatus, setLocalStatus] = useState(task.Task_Status);
  const [localProof,  setLocalProof]  = useState(task.Proof_File || "");
  const [crForm, setCrForm] = useState({ fileNumbers:"", dateOfCall:"", clientName:"", strengths:"", areasOfImprovement:"", planOfAction:"" });
  const [ooForm, setOoForm] = useState({ notes:"" });
  const [ooo,    setOoo]    = useState(false);
  const pasteRef = useRef(null);

  useEffect(() => {
    if (autoExpand) {
      setExpanded(true);
      if (onAutoExpanded) onAutoExpanded();
    }
  }, [autoExpand, onAutoExpanded]);

  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type.startsWith("image/")) {
        const blob = item.getAsFile();
        const reader = new FileReader();
        reader.onload = (ev) => {
          const img = new Image();
          img.onload = () => {
            const canvas = document.createElement("canvas");
            const maxW = 600;
            const scale = Math.min(1, maxW / img.width);
            canvas.width = img.width * scale;
            canvas.height = img.height * scale;
            canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
            // Try progressively lower quality until under 35KB
            let quality = 0.5;
            let dataUrl = canvas.toDataURL("image/jpeg", quality);
            while (dataUrl.length > 35000 && quality > 0.1) {
              quality -= 0.1;
              dataUrl = canvas.toDataURL("image/jpeg", quality);
            }
            setProofImg(dataUrl);
          };
          img.src = ev.target.result;
        };
        reader.readAsDataURL(blob);
        e.preventDefault();
        break;
      }
    }
  };

  const handleSubmit = async () => {
    if (!isDirector && isSpotCheck && !ooo && !notes.trim()) {
      alert("Please add your spot check notes before submitting.");
      return;
    }
    if (!isDirector && !isFormTask && !isSimpleDailyTask && !isEscalationTask && !proofImg) {
      alert("Please paste a screenshot as proof before submitting.");
      return;
    }
    if (!isDirector && isCallReview) {
      if (!crForm.fileNumbers || !crForm.dateOfCall || !crForm.strengths || !crForm.areasOfImprovement || !crForm.planOfAction) {
        alert("Please fill out all call review fields before submitting.");
        return;
      }
    }
    setSubmitting(true);
    const newStatus = isDirector && localStatus === "Pending Review" ? "Completed" : "Pending Review";
    const formNotes = isCallReview
      ? JSON.stringify({type:"CALL_REVIEW",...crForm, agent:task.Agent_Name, team:task.Team, manager:task.Manager})
      : isOneOnOne
        ? JSON.stringify({type:tType, notes:ooForm.notes, agent:task.Agent_Name, team:task.Team, manager:task.Team, sessionNumber:tType==="ONE_ON_ONE_1"?"1":"2"})
        : isSpotCheck
          ? (ooo ? "[OUT OF OFFICE]" : notes)
          : notes;
    const proofData = isFormTask ? "FORM_SUBMITTED" : isSimpleDailyTask ? "CHECKBOX_SUBMITTED" : isEscalationTask ? "ESCALATION_SUBMITTED" : (proofImg || localProof);
    const payload = { status: newStatus, completionNotes: formNotes, proofData: proofData, completedBy: userEmail };

    // On director approval of form tasks, include structured data for archiving
    if (isDirector && newStatus === "Completed" && localStatus === "Pending Review") {
      try {
        const parsed = JSON.parse(task.Completion_Notes || "{}");
        if (parsed.type === "CALL_REVIEW") payload.callReviewData = parsed;
        if (parsed.type === "ONE_ON_ONE_1" || parsed.type === "ONE_ON_ONE_2") payload.oneOnOneData = parsed;
      } catch(e) {}
    }

    const result = await updateTask(sheetName, task._rowIndex, payload);
    if (result.success) {
      setLocalStatus(newStatus);
      setLocalProof(proofData);
      setProofImg("");
      if (onUpdated) onUpdated(task._rowIndex, newStatus, notes);
    }
    setSubmitting(false);
  };

  const handleReopen = async () => {
    setSubmitting(true);
    const result = await updateTask(sheetName, task._rowIndex, { status:"Open", completionNotes:"", proofData:"", completedBy:"" });
    if (result.success) { setLocalStatus("Open"); setLocalProof(""); setProofImg(""); setNotes(""); if (onUpdated) onUpdated(task._rowIndex, "Open", ""); }
    setSubmitting(false);
  };

  const tType     = task[typeCol] || task.Task_Type || "";
  const isDone    = localStatus === "Completed";
  const isPending = localStatus === "Pending Review";
  const isWeeklyTask = typeCol === "Weekly_Flag" || sheetName === "WEEKLY_MANAGER_TASKS";
  const isMtdTask    = typeCol === "MTD_Flag"    || sheetName === "MTD_MANAGER_TASKS";
  const KNOWN_TYPES = ["DONUT_CHAT_GROUP","ZERO_CONVERSION","LOW_CONVERSION","STREAK_2","STREAK_3","STREAK_4","STREAK_5","FREQ_2_OF_3","FREQ_3_OF_5","SATURDAY_SIGNUPS_REVIEW","MORNING_MESSAGE_PREP","AGENT_TIME_DETAIL","FOLLOWUP_CALLS","WEEKLY_DOUBLE_MISS","WEEKLY_LOW_PRODUCTION","WEEKLY_LOW_CONVERSION","MTD_TRIPLE_MISS","MTD_PRODUCTION_CONVERSION","MTD_PRODUCTION_CANCEL","MTD_CONVERSION_CANCEL","MTD_LOW_PRODUCTION","MTD_LOW_CONVERSION","MTD_HIGH_CANCEL","GOALS_EMAIL","CALL_REVIEW","ONE_ON_ONE_1","ONE_ON_ONE_2","DAILY_SPOT_CHECK","COMPLIANCE_CHECK_1","COMPLIANCE_CHECK_2","COMPLIANCE_CHECK_3","COMPLIANCE_CHECK_4"];
  const isManualTask = !isWeeklyTask && !isMtdTask && tType && !KNOWN_TYPES.includes(tType);
  const isCallReview  = tType === "CALL_REVIEW" || task.Task_Type === "CALL_REVIEW";
  const isOneOnOne    = tType === "ONE_ON_ONE_1" || tType === "ONE_ON_ONE_2" || task.Task_Type === "ONE_ON_ONE_1" || task.Task_Type === "ONE_ON_ONE_2";
  const isSpotCheck   = ["DAILY_SPOT_CHECK","COMPLIANCE_CHECK_1","COMPLIANCE_CHECK_2","COMPLIANCE_CHECK_3","COMPLIANCE_CHECK_4"].includes(tType) || ["DAILY_SPOT_CHECK","COMPLIANCE_CHECK_1","COMPLIANCE_CHECK_2","COMPLIANCE_CHECK_3","COMPLIANCE_CHECK_4"].includes(task.Task_Type);
  const isFormTask    = isCallReview || isOneOnOne || isManualTask || isSpotCheck;
  // Escalation tasks — require Director sign-off to clear
  const ESCALATION_TYPES = ["STREAK_3","STREAK_4","STREAK_5","FREQ_3_OF_5"];
  const isEscalationTask = ESCALATION_TYPES.includes(tType) || ESCALATION_TYPES.includes(task.Task_Type);
  // Simple daily checkbox tasks — no proof required (all daily known types except escalation, call review, 1-on-1, spot check)
  const SIMPLE_DAILY_TYPES = ["DONUT_CHAT_GROUP","ZERO_CONVERSION","LOW_CONVERSION","STREAK_2","FREQ_2_OF_3","MORNING_MESSAGE_PREP","AGENT_TIME_DETAIL","FOLLOWUP_CALLS","CLEAR_TIMESHEETS","SATURDAY_SIGNUPS_REVIEW","WEEKLY_GOALS_UPDATE","MID_MONTH_UPDATE"];
  const isSimpleDailyTask = !isWeeklyTask && !isMtdTask && (SIMPLE_DAILY_TYPES.includes(tType) || SIMPLE_DAILY_TYPES.includes(task.Task_Type));

  return (
    <>
      <tr onClick={() => setExpanded(!expanded)} style={{ borderBottom:"1px solid #1a2035", background:isDone?"#0f1a10":isPending?"#1a1030":isManualTask&&!isDone&&!isPending?"rgba(0,212,255,0.06)":"transparent", cursor:"pointer" }}>
        {checkboxCell || null}
        {sourceLabel && <td style={{ padding:"10px 16px" }}><span style={{ fontSize:11, fontWeight:700, background:"#2a3148", color:"#94a3b8", padding:"2px 6px", borderRadius:4 }}>{sourceLabel}</span></td>}
        <td style={{ padding:"10px 16px", color:isDone?"#4ade80":isPending?"#a78bfa":"#e2e8f0", fontWeight:500 }}>{friendlyType(tType)}</td>
        <td style={{ padding:"10px 16px", color:"#94a3b8" }}>
          <div>{task.Agent_Name || task.Agents_List || "—"}</div>
          {task.Flag_Reason && <div style={{ fontSize:11, color:"#475569", marginTop:2 }}>{task.Flag_Reason}</div>}
        </td>
        <td style={{ padding:"10px 16px", color:"#94a3b8", maxWidth:200 }}>{task.Required_Action || "—"}</td>
        <td style={{ padding:"10px 16px" }}><Bdg cls={priorityBadge(task.Priority)}>{task.Priority}</Bdg></td>
        <td style={{ padding:"10px 16px", color:"#64748b", fontSize:12 }}>{task.Due_Time || "EOD"}</td>
        <td style={{ padding:"10px 16px" }}><Bdg cls={statusBadge(localStatus)}>{localStatus}</Bdg></td>
        <td style={{ padding:"10px 16px" }}>
          {isSimpleDailyTask
            ? <Bdg cls={isDone||isPending?"bg-green-500/20 text-green-400 border-green-500/40":"bg-slate-500/20 text-slate-400 border-slate-500/40"}>{isDone||isPending?"✅ Done":"—"}</Bdg>
            : isEscalationTask
              ? <Bdg cls={isDone?"bg-green-500/20 text-green-400 border-green-500/40":isPending?"bg-red-500/20 text-red-400 border-red-500/40":"bg-red-500/20 text-red-400 border-red-500/40"}>{isDone?"✅ Cleared":isPending?"🚨 Needs Sign-off":"🚨 Escalate"}</Bdg>
              : <Bdg cls={proofBadge(localProof?"Submitted":task.Proof_Status)}>{localProof?"Submitted":(task.Proof_Status||"Missing")}</Bdg>
          }
        </td>
        <td style={{ padding:"10px 16px", color:"#475569", fontSize:12 }}>{expanded?"▲":"▼"}</td>
      </tr>

      {expanded && (
        <tr style={{ borderBottom:"1px solid #2a3148" }}>
          <td colSpan={8 + (checkboxCell?1:0) + (sourceLabel?1:0)} style={{ padding:0 }}>
            <div style={{ background:"#151c2e", padding:20, display:"flex", flexDirection:"column", gap:16 }}>
              {/* Task details grid */}
              <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:12 }}>
                {[
                  {label:"Task Type",       value:friendlyType(tType)},
                  {label:"Agent",           value:task.Agent_Name||task.Agents_List||"—"},
                  {label:"Required Action", value:task.Required_Action||"—"},
                  {label:"Flag Reason",     value:task.Flag_Reason||task.Weekly_Flag||task.MTD_Flag||"—"},
                  {label:"Priority",        value:task.Priority},
                  {label:"Due",             value:task.Due_Time||"EOD"},
                ].map(f=>(
                  <div key={f.label} style={{ background:"#1e2840", borderRadius:8, padding:"10px 12px" }}>
                    <div style={{ fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.08em", color:"#475569", marginBottom:4 }}>{f.label}</div>
                    <div style={{ fontSize:13, color:"#e2e8f0" }}>{f.value}</div>
                  </div>
                ))}
              </div>

              {/* Form or proof area */}
              {!isDone && isCallReview && (
                <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
                  <div style={{ fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.08em", color:"#a78bfa", marginBottom:4 }}>📞 Call Review Form</div>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                    {[
                      {key:"fileNumbers",  label:"File Number(s)",  placeholder:"e.g. 12345"},
                      {key:"dateOfCall",   label:"Date of Call",    placeholder:"e.g. 05/15/2026"},
                      {key:"clientName",   label:"Client Name",     placeholder:"e.g. Jane Doe"},
                    ].map(f=>(
                      <div key={f.key}>
                        <div style={{ fontSize:11, fontWeight:600, color:"#64748b", marginBottom:4 }}>{f.label}</div>
                        <input value={crForm[f.key]||""} onChange={e=>setCrForm(p=>({...p,[f.key]:e.target.value}))}
                          placeholder={f.placeholder}
                          style={{ width:"100%", background:"#1e2840", border:"1px solid #2a3148", borderRadius:8, padding:"8px 12px", color:"#e2e8f0", fontSize:13, outline:"none", boxSizing:"border-box" }}/>
                      </div>
                    ))}
                  </div>
                  {[
                    {key:"strengths",          label:"Strengths / Best Practices",                                              color:"#34d399"},
                    {key:"areasOfImprovement", label:"Areas of Improvement",                                                    color:"#fbbf24"},
                    {key:"planOfAction",       label:"Plan of Action (Give them a plan & Set a specific Target/Pace!)",         color:"#a78bfa"},
                  ].map(f=>(
                    <div key={f.key}>
                      <div style={{ fontSize:11, fontWeight:600, color:f.color, marginBottom:4 }}>{f.label}</div>
                      <textarea value={crForm[f.key]||""} onChange={e=>setCrForm(p=>({...p,[f.key]:e.target.value}))}
                        placeholder={`Enter ${f.label.toLowerCase()}...`}
                        style={{ width:"100%", background:"#1e2840", border:"1px solid #2a3148", borderRadius:8, padding:"8px 12px", color:"#e2e8f0", fontSize:13, resize:"vertical", minHeight:72, outline:"none", fontFamily:"inherit", boxSizing:"border-box" }}/>
                    </div>
                  ))}
                  {/* Email draft preview */}
                  {(crForm.strengths || crForm.areasOfImprovement || crForm.planOfAction) && (
                    <div style={{ marginTop:8 }}>
                      <div style={{ fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.08em", color:"#00d4ff", marginBottom:6 }}>Agent Email Draft</div>
                      <div style={{ background:"#111827", border:"1px solid rgba(0,212,255,0.2)", borderRadius:8, padding:"14px 16px", fontSize:13, color:"#e2e8f0", lineHeight:1.8, fontFamily:"inherit" }}>
                        <div>Hi {(task.Agent_Name || "[Agent Name]").split(" ")[0]},</div>
                        <br/>
                        <div>As Discussed, I took the time to analyze your calls and see what we can do to surpass our goals. Please see below for my notes and feedback. I will continue to be here for you and work on reaching new heights together, lets have a huge month!</div>
                        <br/>
                        <div><strong>Date of Call:</strong> {crForm.dateOfCall || ""}</div>
                        <div><strong>File #:</strong> {crForm.fileNumbers || ""}</div>
                        <div><strong>Client Name:</strong> {crForm.clientName || ""}</div>
                        <br/>
                        <div><strong>Strengths/Best Practices (What we are doing great on):</strong></div>
                        <div style={{ whiteSpace:"pre-wrap" }}>{crForm.strengths || ""}</div>
                        <br/>
                        <div><strong>Areas of Improvement (What we can do better):</strong></div>
                        <div style={{ whiteSpace:"pre-wrap" }}>{crForm.areasOfImprovement || ""}</div>
                        <br/>
                        <div><strong>Plan of Action (Notes/Goal for the Month):</strong></div>
                        <div style={{ whiteSpace:"pre-wrap" }}>{crForm.planOfAction || ""}</div>
                        <br/>
                        <div>{userName || "Your Manager"}</div>
                      </div>
                      <button onClick={()=>{
                        const html = `<p>Hi ${(task.Agent_Name || "[Agent Name]").split(" ")[0]},</p><br><p>As Discussed, I took the time to analyze your calls and see what we can do to surpass our goals. Please see below for my notes and feedback. I will continue to be here for you and work on reaching new heights together, lets have a huge month!</p><br><p><strong>Date of Call:</strong> ${crForm.dateOfCall || ""}</p><p><strong>File #:</strong> ${crForm.fileNumbers || ""}</p><p><strong>Client Name:</strong> ${crForm.clientName || ""}</p><br><p><strong>Strengths/Best Practices (What we are doing great on):</strong></p><p>${(crForm.strengths||"").replace(/\n/g,"<br>")}</p><br><p><strong>Areas of Improvement (What we can do better):</strong></p><p>${(crForm.areasOfImprovement||"").replace(/\n/g,"<br>")}</p><br><p><strong>Plan of Action (Notes/Goal for the Month):</strong></p><p>${(crForm.planOfAction||"").replace(/\n/g,"<br>")}</p><br><p>${userName||"Your Manager"}</p>`;
                        const plain = `Hi ${task.Agent_Name || "[Agent Name]"},\n\nAs Discussed, I took the time to analyze your calls and see what we can do to surpass our goals. Please see below for my notes and feedback. I will continue to be here for you and work on reaching new heights together, lets have a huge month!\n\nDate of Call: ${crForm.dateOfCall||""}\nFile #: ${crForm.fileNumbers||""}\nClient Name: ${crForm.clientName||""}\n\nStrengths/Best Practices (What we are doing great on):\n${crForm.strengths||""}\n\nAreas of Improvement (What we can do better):\n${crForm.areasOfImprovement||""}\n\nPlan of Action (Notes/Goal for the Month):\n${crForm.planOfAction||""}\n\n${userName||"Your Manager"}`;
                        try {
                          const blob = new Blob([html], {type:"text/html"});
                          const plainBlob = new Blob([plain], {type:"text/plain"});
                          navigator.clipboard.write([new ClipboardItem({"text/html":blob,"text/plain":plainBlob})]).then(()=>alert("Copied! Paste into your email — bold formatting will carry over in Outlook/Gmail.")).catch(()=>{
                            navigator.clipboard.writeText(plain).then(()=>alert("Copied as plain text."));
                          });
                        } catch(e) {
                          navigator.clipboard.writeText(plain).then(()=>alert("Copied as plain text."));
                        }
                      }} style={{ marginTop:8, background:"rgba(0,212,255,0.1)", color:"#00d4ff", border:"1px solid rgba(0,212,255,0.3)", borderRadius:8, padding:"6px 16px", fontSize:12, fontWeight:600, cursor:"pointer" }}>
                        Copy to Clipboard
                      </button>
                    </div>
                  )}
                </div>
              )}

              {!isDone && isOneOnOne && (
                <div>
                  <div style={{ fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.08em", color:"#a78bfa", marginBottom:8 }}>🤝 Session Notes</div>
                  <textarea value={ooForm.notes} onChange={e=>setOoForm({notes:e.target.value})}
                    placeholder="Log what was discussed, areas of improvement, follow-ups, etc..."
                    style={{ width:"100%", background:"#1e2840", border:"1px solid #2a3148", borderRadius:8, padding:"8px 12px", color:"#e2e8f0", fontSize:13, resize:"vertical", minHeight:120, outline:"none", fontFamily:"inherit", boxSizing:"border-box" }}/>
                </div>
              )}

              {/* Spot check form */}
              {!isDone && isSpotCheck && (
                <SpotCheckForm notes={notes} setNotes={setNotes} ooo={ooo} setOoo={setOoo}/>
              )}

              {/* Manual task — just notes, no proof needed */}
              {!isDone && isManualTask && (
                <div style={{ background:"rgba(0,212,255,0.05)", border:"1px solid rgba(0,212,255,0.2)", borderRadius:10, padding:16 }}>
                  <div style={{ fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.08em", color:"#00d4ff", marginBottom:8 }}>⚡ Director Task</div>
                  <div style={{ fontSize:14, color:"#e2e8f0", marginBottom:12, lineHeight:1.6 }}>{task.Required_Action}</div>
                  <div style={{ fontSize:12, color:"#64748b" }}>Add notes below and click Submit to acknowledge completion.</div>
                </div>
              )}

              {/* Escalation task — must escalate to Director */}
              {!isDone && isEscalationTask && (
                <div style={{ background:"rgba(239,68,68,0.08)", border:"2px solid rgba(239,68,68,0.4)", borderRadius:10, padding:20 }}>
                  <div style={{ fontSize:13, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.08em", color:"#f87171", marginBottom:10 }}>🚨 Escalation Required</div>
                  <div style={{ fontSize:15, fontWeight:600, color:"#fca5a5", marginBottom:8, lineHeight:1.6 }}>
                    Escalate — schedule meeting with Director + Agent to address
                  </div>
                  <div style={{ fontSize:13, color:"#94a3b8", lineHeight:1.6 }}>
                    This task requires your direct involvement. Contact the Director to schedule a meeting with the agent. Once you submit, the Director must sign off to clear this task.
                  </div>
                  {isPending && !isDirector && (
                    <div style={{ marginTop:12, fontSize:13, color:"#a78bfa", fontWeight:600 }}>⏳ Submitted — awaiting Director sign-off</div>
                  )}
                </div>
              )}

              {/* Simple daily checkbox task — no proof needed */}
              {!isDone && isSimpleDailyTask && (
                <div style={{ background:"rgba(34,197,94,0.06)", border:"1px solid rgba(34,197,94,0.25)", borderRadius:10, padding:16 }}>
                  <div style={{ fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.08em", color:"#4ade80", marginBottom:6 }}>✅ Daily Task</div>
                  <div style={{ fontSize:13, color:"#94a3b8", lineHeight:1.6 }}>{task.Required_Action || "Mark this task complete when done."}</div>
                </div>
              )}

              {/* Proof paste area — only for standard tasks (not form, not simple daily, not escalation) */}
              {!isDone && !isFormTask && !isSimpleDailyTask && !isEscalationTask && (
                <div>
                  <div style={{ fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.08em", color:"#64748b", marginBottom:8 }}>
                    📎 Proof — Click below then Ctrl+V to paste screenshot
                  </div>
                  <div
                    ref={pasteRef} onPaste={handlePaste} tabIndex={0}
                    style={{ background:"#1e2840", border:`2px dashed ${proofImg?"#00d4ff":"#2a3148"}`, borderRadius:8, padding:proofImg?8:24, minHeight:80, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", outline:"none" }}
                    onClick={() => pasteRef.current?.focus()}
                  >
                    {proofImg ? (
                      <div style={{ position:"relative", width:"100%" }}>
                        <img src={proofImg} alt="proof" style={{ maxWidth:"100%", maxHeight:300, borderRadius:6, display:"block", margin:"0 auto" }}/>
                        <button onClick={(e)=>{e.stopPropagation();setProofImg("");}} style={{ position:"absolute", top:4, right:4, background:"rgba(0,0,0,0.7)", border:"none", color:"#f87171", borderRadius:4, padding:"2px 6px", cursor:"pointer", fontSize:12 }}>✕ Remove</button>
                      </div>
                    ) : (
                      <div style={{ textAlign:"center", color:"#475569", fontSize:13 }}>
                        <div style={{ fontSize:24, marginBottom:8 }}>📋</div>
                        <div>Click here, then press <strong style={{ color:"#94a3b8" }}>Ctrl+V</strong> to paste screenshot</div>
                        <div style={{ fontSize:11, marginTop:4, color:"#334155" }}>Take a screenshot with Snipping Tool, then paste here</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Show submitted call review data */}
              {(isPending || isDone) && isCallReview && SubmittedCallReview(task.Completion_Notes)}

              {/* Show submitted 1-on-1 notes */}
              {(isPending || isDone) && isOneOnOne && SubmittedOneOnOne(task.Completion_Notes)}

              {/* Show submitted proof */}
              {(isPending || isDone) && localProof && (
                <div>
                  <div style={{ fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.08em", color:"#a78bfa", marginBottom:8 }}>📎 Submitted Proof</div>
                  {localProof.startsWith("https://") ? (
                    <a href={localProof} target="_blank" rel="noreferrer" style={{ display:"inline-flex", alignItems:"center", gap:8, background:"rgba(139,92,246,0.15)", border:"1px solid rgba(139,92,246,0.4)", borderRadius:8, padding:"10px 16px", color:"#a78bfa", fontSize:13, fontWeight:600, textDecoration:"none" }}>
                      🖼 View Screenshot →
                    </a>
                  ) : localProof.startsWith("data:") ? (
                    <img src={localProof} alt="proof" style={{ maxWidth:"100%", maxHeight:400, borderRadius:8, display:"block" }}/>
                  ) : (
                    <div style={{ fontSize:13, color:"#94a3b8", background:"#1e2840", borderRadius:8, padding:12 }}>{localProof.substring(0,100)}...</div>
                  )}
                </div>
              )}

              {/* Director note — show if present */}
              {notes.startsWith("[Director]:") && (
                <div style={{ background:"#1a1030", border:"1px solid rgba(139,92,246,0.4)", borderRadius:8, padding:"10px 14px" }}>
                  <div style={{ fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.08em", color:"#a78bfa", marginBottom:6 }}>📝 Note from Director</div>
                  <div style={{ fontSize:13, color:"#e2e8f0" }}>{notes.replace("[Director]:","").trim()}</div>
                </div>
              )}

              {/* Manager notes */}
              <div>
                <div style={{ fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.08em", color:"#64748b", marginBottom:8 }}>📝 Your Notes (optional)</div>
                <textarea
                  value={notes.startsWith("[Director]:") ? "" : notes}
                  onChange={e=>setNotes(e.target.value)} disabled={isDone}
                  placeholder="Add any notes about this task..."
                  style={{ width:"100%", background:"#1e2840", border:"1px solid #2a3148", borderRadius:8, padding:12, color:"#e2e8f0", fontSize:13, resize:"vertical", minHeight:72, outline:"none", fontFamily:"inherit", boxSizing:"border-box", opacity:isDone?0.5:1 }}
                />
              </div>

              {/* Action buttons */}
              <div style={{ display:"flex", gap:12, alignItems:"center" }}>
                {!isDone && !isPending && (
                  <button onClick={handleSubmit} disabled={submitting} style={{ background: isEscalationTask ? "#ef4444" : "#00d4ff", color: isEscalationTask ? "#fff" : "#000", border:"none", borderRadius:8, padding:"8px 20px", fontWeight:700, fontSize:13, cursor:submitting?"not-allowed":"pointer", opacity:submitting?0.7:1 }}>
                    {submitting ? "Submitting..." : isEscalationTask ? "🚨 Submit — Request Director Sign-off" : isSimpleDailyTask ? "✓ Mark Complete" : "✓ Submit for Review"}
                  </button>
                )}
                {isPending && !isDirector && (
                  <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                    <Bdg cls={isEscalationTask ? "bg-red-500/20 text-red-400 border-red-500/40" : "bg-purple-500/20 text-purple-400 border-purple-500/40"}>
                      {isEscalationTask ? "🚨 Pending Director Sign-off" : "⏳ Pending Review"}
                    </Bdg>
                    <span style={{ fontSize:12, color:"#64748b" }}>{isEscalationTask ? "Director must approve to clear" : "Waiting for director approval"}</span>
                  </div>
                )}
                {isPending && isDirector && (
                  <>
                    <button onClick={handleSubmit} disabled={submitting} style={{ background:"#22c55e", color:"#000", border:"none", borderRadius:8, padding:"8px 20px", fontWeight:700, fontSize:13, cursor:submitting?"not-allowed":"pointer" }}>
                      {submitting ? "..." : isEscalationTask ? "✓ Sign Off — Mark Complete" : "✓ Approve — Mark Complete"}
                    </button>
                    <button onClick={handleReopen} disabled={submitting} style={{ background:"transparent", color:"#f87171", border:"1px solid #3a1a1a", borderRadius:8, padding:"8px 16px", fontWeight:600, fontSize:13, cursor:"pointer" }}>
                      ↩ Send Back
                    </button>
                  </>
                )}
                {isDone && (
                  <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                    <Bdg cls="bg-green-500/20 text-green-400 border-green-500/40">✅ Completed</Bdg>
                    {task.Completed_By && <span style={{ fontSize:12, color:"#64748b" }}>by {task.Completed_By}</span>}
                    {isDirector && <button onClick={handleReopen} disabled={submitting} style={{ background:"transparent", color:"#f87171", border:"1px solid #3a1a1a", borderRadius:8, padding:"4px 12px", fontWeight:600, fontSize:12, cursor:"pointer", marginLeft:8 }}>↩ Reopen</button>}
                  </div>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ─── MORNING PREP ─────────────────────────────────────────────────────────────
function PrepView({ tasks, userEmail, userName, isDirector }) {
  if (!tasks || tasks.length === 0) return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", padding:64, gap:12 }}>
      <div style={{ fontSize:32 }}>✅</div>
      <div style={{ color:"#475569", fontSize:14 }}>No morning prep tasks found.</div>
    </div>
  );

  const msgTasks    = tasks.filter(t => (t.Task_Type||"").includes("MORNING"));
  const actionTasks = tasks.filter(t => !(t.Task_Type||"").includes("MORNING"));

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
      {/* Morning Message — informational card */}
      {msgTasks.map((t, i) => (
        <div key={i} style={{ background:"#1e2840", border:"1px solid #2a3148", borderRadius:16, padding:20 }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16 }}>
            <div style={{ display:"flex", alignItems:"center", gap:12 }}>
              <span style={{ fontSize:20 }}>☀️</span>
              <div>
                <div style={{ fontWeight:600, color:"#fff" }}>{t.Manager} — Morning Message Prep</div>
                <div style={{ fontSize:12, color:"#64748b", marginTop:2 }}>{t.Team} · Due {t.Due_Time || "10:00 AM"}</div>
              </div>
            </div>
            <div style={{ display:"flex", gap:8 }}>
              <Bdg cls={statusBadge(t.Task_Status)}>{t.Task_Status}</Bdg>
              <Bdg cls={priorityBadge(t.Priority)}>{t.Priority}</Bdg>
            </div>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
            <div style={{ background:"#151c2e", border:"1px solid #2a3148", borderRadius:8, padding:16 }}>
              <div style={{ fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.1em", color:"#00d4ff", marginBottom:8 }}>🏆 Top Conversion</div>
              {t.Top_Conversion_Shoutouts && t.Top_Conversion_Shoutouts !== "None"
                ? t.Top_Conversion_Shoutouts.split("\n").map((line, j) => <div key={j} style={{ fontSize:13, color:"#e2e8f0", padding:"4px 0", borderBottom:j<t.Top_Conversion_Shoutouts.split("\n").length-1?"1px solid #2a3148":"none" }}>{line}</div>)
                : <div style={{ fontSize:13, color:"#475569" }}>No top converters today</div>}
            </div>
            <div style={{ background:"#151c2e", border:"1px solid #2a3148", borderRadius:8, padding:16 }}>
              <div style={{ fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.1em", color:"#fbbf24", marginBottom:8 }}>⭐ Top Producers</div>
              {t.Top_Producer_Shoutouts && t.Top_Producer_Shoutouts !== "None"
                ? t.Top_Producer_Shoutouts.split("\n").map((line, j) => <div key={j} style={{ fontSize:13, color:"#e2e8f0", padding:"4px 0", borderBottom:j<t.Top_Producer_Shoutouts.split("\n").length-1?"1px solid #2a3148":"none" }}>{line}</div>)
                : <div style={{ fontSize:13, color:"#475569" }}>No top producers today</div>}
            </div>
          </div>
          {t.Yesterday_Stats && (
            <div style={{ background:"#151c2e", border:"1px solid #2a3148", borderRadius:8, padding:16, marginTop:12 }}>
              <div style={{ fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.1em", color:"#34d399", marginBottom:10 }}>📊 Yesterday's Team Stats</div>
              {(() => {
                let rows = null;
                try { rows = JSON.parse(t.Yesterday_Stats); } catch(e) {}
                if (!rows || !Array.isArray(rows)) return <div style={{ fontSize:13, color:"#475569" }}>No stats available</div>;
                return (
                  <div style={{ overflowX:"auto" }}>
                    <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
                      <thead>
                        <tr style={{ borderBottom:"1px solid #2a3148" }}>
                          {["Team","Signed","Deals/Agt","Inbound/Agent","% Same Day","% Inb Same Day","No Deal","No Deal %"].map(h=>(
                            <th key={h} style={{ padding:"5px 10px", textAlign:"left", fontSize:10, fontWeight:700, color:"#475569", textTransform:"uppercase", letterSpacing:"0.06em" }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((row,i)=>{
                          const dpa = parseFloat(String(row.dealsPerAgent).replace(/[^0-9.]/g,""));
                          const sds = parseFloat(String(row.sameDaySigned).replace(/[^0-9.]/g,""));
                          const ndp = parseFloat(String(row.noDealPct).replace(/[^0-9.]/g,""));
                          const dpaColor = isNaN(dpa)?"#e2e8f0":dpa>=1.5?"#34d399":dpa>=1.0?"#fbbf24":"#f87171";
                          const sdsColor = isNaN(sds)?"#e2e8f0":sds>=13?"#34d399":sds>=10?"#fbbf24":"#f87171";
                          const ndpColor = isNaN(ndp)?"#e2e8f0":ndp>20?"#f87171":ndp>=16?"#fbbf24":"#34d399";
                          return (
                          <tr key={i} style={{ borderBottom:"1px solid #1a2035" }}>
                            <td style={{ padding:"6px 10px", color:"#e2e8f0", fontWeight:700 }}>{row.team}</td>
                            <td style={{ padding:"6px 10px", color:"#34d399", fontWeight:600 }}>{row.signed}</td>
                            <td style={{ padding:"6px 10px", color:dpaColor, fontWeight:600 }}>{row.dealsPerAgent}</td>
                            <td style={{ padding:"6px 10px", color:"#94a3b8" }}>{row.inboundCallsPerAgent}</td>
                            <td style={{ padding:"6px 10px", color:sdsColor, fontWeight:600 }}>{row.sameDaySigned}</td>
                            <td style={{ padding:"6px 10px", color:"#00d4ff" }}>{row.inboundSameDay}</td>
                            <td style={{ padding:"6px 10px", color:ndpColor, fontWeight:600 }}>{row.noDeals}</td>
                            <td style={{ padding:"6px 10px", color:ndpColor, fontWeight:600 }}>{row.noDealPct}</td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                );
              })()}
            </div>
          )}
          {t.Reminders && (
            <div style={{ background:"#151c2e", border:"1px solid #2a3148", borderRadius:8, padding:16, marginTop:12 }}>
              <div style={{ fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.1em", color:"#94a3b8", marginBottom:8 }}>📋 Reminders</div>
              {t.Reminders.split("\n").map((line, j) => (
                <div key={j} style={{ fontSize:13, color:"#94a3b8", padding:"3px 0", display:"flex", gap:8 }}><span style={{ color:"#475569" }}>•</span>{line}</div>
              ))}
            </div>
          )}
        </div>
      ))}

      {/* Submittable proof tasks */}
      {actionTasks.length > 0 && (
        <div style={{ border:"1px solid #2a3148", borderRadius:12, overflow:"hidden" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
            <thead>
              <tr style={{ background:"#151c2e", borderBottom:"1px solid #2a3148" }}>
                {["Type","Agent / Group","Action","Priority","Due","Status","Proof",""].map(h => (
                  <th key={h} style={{ textAlign:"left", padding:"10px 16px", fontSize:11, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.05em", color:"#475569" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {actionTasks.map((t, i) => (
                <TaskRow key={t._rowIndex||i} task={t} typeCol="Task_Type" sheetName="DAILY_MANAGER_PREP_TASKS" userEmail={userEmail} userName={userName} onUpdated={()=>{}} isDirector={isDirector}/>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── TASK TABLE ───────────────────────────────────────────────────────────────
function TaskTable({ tasks, typeCol, sheetName, userEmail, userName, isDirector, expandTask, onExpanded }) {
  const [filter, setFilter] = useState("Open");
  const [localTasks, setLocalTasks] = useState(tasks);
  const pendingCount = localTasks.filter(t => t.Task_Status === "Pending Review").length;
  const priorityOrder = {"Critical":1,"High":2,"Medium":3,"Low":4};
  const filtered = (filter === "All" ? localTasks : localTasks.filter(t => t.Task_Status === filter))
    .slice().sort((a,b) => {
      const aManual = a.Task_Type && !["DONUT_CHAT_GROUP","ZERO_CONVERSION","LOW_CONVERSION","STREAK_2","STREAK_3","STREAK_4","STREAK_5","FREQ_2_OF_3","FREQ_3_OF_5","SATURDAY_SIGNUPS_REVIEW","MORNING_MESSAGE_PREP","AGENT_TIME_DETAIL","FOLLOWUP_CALLS","WEEKLY_DOUBLE_MISS","WEEKLY_LOW_PRODUCTION","WEEKLY_LOW_CONVERSION","MTD_TRIPLE_MISS","MTD_PRODUCTION_CONVERSION","MTD_PRODUCTION_CANCEL","MTD_CONVERSION_CANCEL","MTD_LOW_PRODUCTION","MTD_LOW_CONVERSION","MTD_HIGH_CANCEL","GOALS_EMAIL","CALL_REVIEW","ONE_ON_ONE_1","ONE_ON_ONE_2","DAILY_SPOT_CHECK","COMPLIANCE_CHECK_1","COMPLIANCE_CHECK_2","COMPLIANCE_CHECK_3","COMPLIANCE_CHECK_4"].includes(a.Task_Type) ? 0 : 1;
      const bManual = b.Task_Type && !["DONUT_CHAT_GROUP","ZERO_CONVERSION","LOW_CONVERSION","STREAK_2","STREAK_3","STREAK_4","STREAK_5","FREQ_2_OF_3","FREQ_3_OF_5","SATURDAY_SIGNUPS_REVIEW","MORNING_MESSAGE_PREP","AGENT_TIME_DETAIL","FOLLOWUP_CALLS","WEEKLY_DOUBLE_MISS","WEEKLY_LOW_PRODUCTION","WEEKLY_LOW_CONVERSION","MTD_TRIPLE_MISS","MTD_PRODUCTION_CONVERSION","MTD_PRODUCTION_CANCEL","MTD_CONVERSION_CANCEL","MTD_LOW_PRODUCTION","MTD_LOW_CONVERSION","MTD_HIGH_CANCEL","GOALS_EMAIL","CALL_REVIEW","ONE_ON_ONE_1","ONE_ON_ONE_2","DAILY_SPOT_CHECK","COMPLIANCE_CHECK_1","COMPLIANCE_CHECK_2","COMPLIANCE_CHECK_3","COMPLIANCE_CHECK_4"].includes(b.Task_Type) ? 0 : 1;
      if (aManual !== bManual) return aManual - bManual;
      return (priorityOrder[a.Priority]??9) - (priorityOrder[b.Priority]??9);
    });

  const handleUpdated = (rowIndex, newStatus, newNotes) => {
    setLocalTasks(prev => prev.map(t => t._rowIndex === rowIndex ? {...t, Task_Status:newStatus, Completion_Notes:newNotes} : t));
  };

  if (!localTasks || localTasks.length === 0) return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", padding:64, gap:12 }}>
      <div style={{ fontSize:32 }}>✅</div>
      <div style={{ color:"#475569", fontSize:14 }}>No tasks found.</div>
    </div>
  );

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
      <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
        {["Open","Pending Review","Completed","All"].map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{ background:filter===f?"rgba(0,212,255,0.1)":"#1e2840", color:filter===f?"#00d4ff":"#64748b", border:`1px solid ${filter===f?"rgba(0,212,255,0.3)":"#2a3148"}`, borderRadius:8, padding:"4px 12px", fontSize:12, fontWeight:500, cursor:"pointer" }}>
            {f}{f==="Pending Review"&&pendingCount>0&&<span style={{ marginLeft:6, background:"#8b5cf6", color:"#fff", borderRadius:9999, padding:"1px 5px", fontSize:10, fontWeight:700 }}>{pendingCount}</span>}
          </button>
        ))}
        <span style={{ fontSize:12, color:"#475569", marginLeft:8 }}>{filtered.length} tasks</span>
      </div>
      <div style={{ border:"1px solid #2a3148", borderRadius:12, overflow:"hidden" }}>
        <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
          <thead>
            <tr style={{ background:"#151c2e", borderBottom:"1px solid #2a3148" }}>
              {["Type","Agent / Group","Action","Priority","Due","Status","Proof",""].map(h => (
                <th key={h} style={{ textAlign:"left", padding:"10px 16px", fontSize:11, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.05em", color:"#475569" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((t, i) => (
              <TaskRow key={t._rowIndex||i} task={t} typeCol={typeCol} sheetName={t._sheetName||sheetName} userEmail={userEmail} userName={userName} onUpdated={handleUpdated} isDirector={isDirector} autoExpand={expandTask === t._rowIndex} onAutoExpanded={expandTask === t._rowIndex ? onExpanded : null}/>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ─── ANIMATED NUMBER ─────────────────────────────────────────────────────────
function AnimatedNumber({ value, color, fontSize=28 }) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    setDisplay(0);
    if (!value || value === 0) return;
    const steps = 40;
    const interval = 900 / steps;
    let count = 0;
    const timer = setInterval(() => {
      count++;
      if (count >= steps) { setDisplay(value); clearInterval(timer); }
      else { setDisplay(Math.round((value / steps) * count)); }
    }, interval);
    return () => clearInterval(timer);
  }, [value]);
  return <span style={{ fontSize, fontWeight:900, color }}>{display.toLocaleString()}</span>;
}

// ─── GOALS TRACKER WIDGET ────────────────────────────────────────────────────
function GoalsTracker({ goalsTracker, onSaved, viewOnly }) {
  const AVG_DEBT = 31500;
  const [tab,     setTab]     = useState("production");
  const [editing, setEditing] = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [loaded,  setLoaded]  = useState(false);
  const g = goalsTracker || {};

  const [settings, setSettings] = useState({
    grossGoalVolume:"", bizDays:"22", numTeams:"7", clearedGoal:"",
    firstPayRate:"55", rollover:"0", schedDaysTotal:"22",
    schedDaysElapsed:"0", lastClearDate:"",
  });
  const [actuals, setActuals] = useState({
    grossSignedVolume:"", dealsSigned:"", currentScheduled:"", currentCleared:"", sameMonthScheduledActual:"",
  });

  useEffect(() => {
    if (loaded) return; // only sync once after data loads
    if (!goalsTracker || Object.keys(goalsTracker).length === 0) return;
    setSettings({
      grossGoalVolume:  goalsTracker.grossGoalVolume  || "",
      bizDays:          goalsTracker.bizDays          || "22",
      numTeams:         goalsTracker.numTeams         || "7",
      clearedGoal:      goalsTracker.clearedGoal      || "",
      firstPayRate:     goalsTracker.firstPayRate     || "55",
      rollover:         goalsTracker.rollover         || "0",
      schedDaysTotal:   goalsTracker.schedDaysTotal   || "22",
      schedDaysElapsed: goalsTracker.schedDaysElapsed || "0",
      lastClearDate:    goalsTracker.lastClearDate    || "",
    });
    setActuals({
      grossSignedVolume:        goalsTracker.grossSignedVolume        || "",
      dealsSigned:              goalsTracker.dealsSigned              || "",
      currentScheduled:         goalsTracker.currentScheduled         || "",
      currentCleared:           goalsTracker.currentCleared           || "",
      sameMonthScheduledActual: goalsTracker.sameMonthScheduledActual || "",
    });
    setLoaded(true);
  }, [goalsTracker]);

  const pf = (k, def=0) => parseFloat(g[k] || def);
  const pi = (k, def=0) => parseInt(g[k] || def);
  const fmt = (n) => {
    if (!n || isNaN(n)) return "—";
    return n >= 1000000 ? `$${(n/1000000).toFixed(2)}M` : n >= 1000 ? `$${(n/1000).toFixed(1)}K` : `$${Math.round(n).toLocaleString()}`;
  };

  // Production calcs
  const grossGoalVol   = pf("grossGoalVolume");
  const bizDays        = pi("bizDays", 22);
  const numTeams       = pi("numTeams", 7);
  const signedVol      = pf("grossSignedVolume");
  const dealsSigned    = pi("dealsSigned");
  const grossGoalUnits = grossGoalVol > 0 ? Math.round(grossGoalVol / AVG_DEBT) : 0;
  const signedUnits    = signedVol > 0 ? Math.round(signedVol / AVG_DEBT) : 0;
  const signedPct      = grossGoalVol > 0 ? (signedVol / grossGoalVol * 100) : 0;
  const dailyVol       = bizDays > 0 ? grossGoalVol / bizDays : 0;
  const weeklyVol      = dailyVol * 5;
  const dailyPerTeam   = numTeams > 0 ? dailyVol / numTeams : 0;
  const weeklyPerTeam  = dailyPerTeam * 5;
  const monthlyPerTeam = numTeams > 0 ? grossGoalVol / numTeams : 0;

  // Payment calcs
  const clearedGoal      = pf("clearedGoal");
  const firstPayRate     = pf("firstPayRate", 55) / 100;
  const rollover         = pf("rollover");
  const grossSchedGoal   = firstPayRate > 0 ? clearedGoal / firstPayRate : 0;
  const sameMonthSched   = grossSchedGoal - rollover;
  const currentSched     = pf("currentScheduled");
  const currentCleared   = pf("currentCleared");
  const schedDaysTotal   = pi("schedDaysTotal", 22);
  const schedDaysElapsed = pi("schedDaysElapsed");
  const schedDaysLeft    = Math.max(0, schedDaysTotal - schedDaysElapsed);
  const dailySchedOrig   = schedDaysTotal > 0 ? sameMonthSched / schedDaysTotal : 0;
  const sameMonthSchedActual = pf("sameMonthScheduledActual");
  const pacedTarget      = dailySchedOrig * schedDaysElapsed;
  const schedDiff        = sameMonthSchedActual - pacedTarget;
  const ahead            = schedDiff >= 0;
  const adjDailyNeeded   = schedDaysLeft > 0 ? Math.max(0, sameMonthSched - sameMonthSchedActual) / schedDaysLeft : 0;
  const estTotalCleared  = firstPayRate > 0 ? (currentSched) * firstPayRate + rollover : 0;
  const clearedPct       = clearedGoal > 0 ? (currentCleared / clearedGoal * 100) : 0;
  const pctColor = (p) => p >= 100 ? "#34d399" : p >= 60 ? "#fbbf24" : "#f87171";

  const handleSave = async () => {
    setSaving(true);
    const result = await saveGoalsTracker({ ...settings, ...actuals });
    if (result.success && onSaved) onSaved();
    setSaving(false);
    setEditing(false);
  };

  const Row = ({label, val, sub, highlight}) => (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"5px 0", borderBottom:"1px solid #1a2035" }}>
      <span style={{ fontSize:11, color:"#64748b" }}>{label}</span>
      <div style={{ textAlign:"right" }}>
        <span style={{ fontSize:12, fontWeight:700, color: highlight || "#e2e8f0" }}>{val}</span>
        {sub && <span style={{ fontSize:10, color:"#475569", marginLeft:6 }}>{sub}</span>}
      </div>
    </div>
  );

  const Stat = ({label, val, sub, pct}) => (
    <div style={{ background:"#151c2e", borderRadius:8, padding:"8px 12px" }}>
      <div style={{ fontSize:9, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.08em", color:"#475569", marginBottom:2 }}>{label}</div>
      <div style={{ fontSize:15, fontWeight:900, color: pct !== undefined ? pctColor(pct) : "#e2e8f0" }}>{val}</div>
      {sub && <div style={{ fontSize:10, color:"#64748b", marginTop:1 }}>{sub}</div>}
      {pct !== undefined && (
        <div style={{ background:"#1e2840", borderRadius:999, height:3, marginTop:4, overflow:"hidden" }}>
          <div style={{ width:`${Math.min(pct,100)}%`, height:"100%", background:pctColor(pct), borderRadius:999 }}/>
        </div>
      )}
    </div>
  );

  const Inp = ({k, obj, set, label, ph="0"}) => (
    <div>
      <div style={{ fontSize:9, fontWeight:700, textTransform:"uppercase", color:"#475569", marginBottom:3 }}>{label}</div>
      <input value={obj[k]} onChange={e=>set(p=>({...p,[k]:e.target.value}))} placeholder={ph}
        style={{ width:"100%", background:"#0f1520", border:"1px solid #2a3148", borderRadius:6, padding:"5px 8px", color:"#e2e8f0", fontSize:12, outline:"none", boxSizing:"border-box" }}/>
    </div>
  );

  return (
    <div style={{ background:"#1e2840", border:"1px solid #2a3148", borderRadius:16, padding:16, display:"flex", flexDirection:"column", gap:10, height:"100%", boxSizing:"border-box" }}>
      {/* Header */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <span style={{ fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.12em", color:"#eab308" }}>📊 Company Monthly Goal</span>
        <div style={{ display:"flex", gap:6, alignItems:"center" }}>
          {!editing && (
            <div style={{ display:"flex", gap:2, background:"#151c2e", border:"1px solid #2a3148", borderRadius:8, padding:2 }}>
              {["production","payments"].map(t=>(
                <button key={t} onClick={()=>setTab(t)} style={{ background:tab===t?"#eab308":"transparent", color:tab===t?"#000":"#64748b", border:"none", borderRadius:6, padding:"3px 10px", fontSize:11, fontWeight:700, cursor:"pointer" }}>
                  {t === "production" ? "Production" : "Payments"}
                </button>
              ))}
            </div>
          )}
          {!viewOnly && (
          <button onClick={()=>setEditing(v=>!v)} style={{ background:"none", border:"1px solid #2a3148", borderRadius:6, padding:"3px 10px", color:"#64748b", fontSize:11, cursor:"pointer" }}>
            {editing ? "✕" : "⚙"}
          </button>
          )}
        </div>
      </div>

      {/* Edit Panel */}
      {editing && !viewOnly && (
        <div style={{ display:"flex", flexDirection:"column", gap:8, flex:1, overflowY:"auto" }}>
          <div style={{ fontSize:9, fontWeight:700, textTransform:"uppercase", color:"#eab308", letterSpacing:"0.1em" }}>Settings</div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:6 }}>
            <Inp k="grossGoalVolume"  obj={settings} set={setSettings} label="Gross Goal $"       ph="160000000"/>
            <Inp k="bizDays"          obj={settings} set={setSettings} label="Biz Days"           ph="22"/>
            <Inp k="numTeams"         obj={settings} set={setSettings} label="# Teams"            ph="7"/>
            <Inp k="clearedGoal"      obj={settings} set={setSettings} label="Cleared Goal $"     ph="125000000"/>
            <Inp k="firstPayRate"     obj={settings} set={setSettings} label="First Pay Rate %"   ph="55"/>
            <Inp k="rollover"         obj={settings} set={setSettings} label="Rollover $"         ph="0"/>
            <Inp k="schedDaysTotal"   obj={settings} set={setSettings} label="Sched Days Total"   ph="22"/>
            <Inp k="schedDaysElapsed" obj={settings} set={setSettings} label="Sched Days Elapsed" ph="0"/>
            <Inp k="lastClearDate"    obj={settings} set={setSettings} label="Clear Date"         ph="Apr 28"/>
          </div>
          <div style={{ fontSize:9, fontWeight:700, textTransform:"uppercase", color:"#eab308", letterSpacing:"0.1em" }}>Daily Actuals</div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:6 }}>
            <Inp k="grossSignedVolume" obj={actuals} set={setActuals} label="Gross Signed $"      ph="0"/>
            <Inp k="dealsSigned"       obj={actuals} set={setActuals} label="Deals Signed"        ph="0"/>
            <Inp k="currentScheduled"          obj={actuals} set={setActuals} label="Current Scheduled (Total) $"      ph="0"/>
            <Inp k="sameMonthScheduledActual"  obj={actuals} set={setActuals} label="Same-Month Scheduled (Actual) $" ph="0"/>
            <Inp k="currentCleared"            obj={actuals} set={setActuals} label="Current Cleared $"                ph="0"/>
          </div>
          <button onClick={handleSave} disabled={saving}
            style={{ background:"#eab308", color:"#000", border:"none", borderRadius:8, padding:"7px 16px", fontWeight:700, fontSize:12, cursor:saving?"not-allowed":"pointer", marginTop:"auto" }}>
            {saving ? "Saving..." : "💾 Save & Refresh"}
          </button>
        </div>
      )}

      {/* Production Tab */}
      {!editing && tab === "production" && (
        <div style={{ display:"flex", flexDirection:"column", gap:8, flex:1 }}>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:6 }}>
            <Stat label="Gross Goal" val={fmt(grossGoalVol)} sub={`${grossGoalUnits} units`}/>
            <Stat label="Gross Signed" val={fmt(signedVol)} sub={`${signedUnits} units · ${signedPct.toFixed(1)}%`} pct={signedPct}/>
          </div>
          <div style={{ background:"#151c2e", borderRadius:8, padding:"8px 12px", flex:1 }}>
            <div style={{ fontSize:9, fontWeight:700, textTransform:"uppercase", color:"#eab308", letterSpacing:"0.1em", marginBottom:6 }}>Targets</div>
            <Row label="Daily Goal"         val={fmt(dailyVol)}       sub={`${Math.round(dailyVol/AVG_DEBT)} units`}/>
            <Row label="Weekly Goal"        val={fmt(weeklyVol)}      sub={`${Math.round(weeklyVol/AVG_DEBT)} units`}/>
            <Row label="Per Team / Day"     val={fmt(dailyPerTeam)}   sub={`${Math.round(dailyPerTeam/AVG_DEBT)} units`}/>
            <Row label="Per Team / Week"    val={fmt(weeklyPerTeam)}  sub={`${Math.round(weeklyPerTeam/AVG_DEBT)} units`}/>
            <Row label="Per Team / Month"   val={fmt(monthlyPerTeam)} sub={`${Math.round(monthlyPerTeam/AVG_DEBT)} units`}/>
          </div>
        </div>
      )}

      {/* Payments Tab */}
      {!editing && tab === "payments" && (
        <div style={{ display:"flex", flexDirection:"column", gap:8, flex:1 }}>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:6 }}>
            <Stat label="Cleared Goal"     val={fmt(clearedGoal)}    sub={g.lastClearDate||"—"}/>
            <Stat label="Current Cleared"  val={fmt(currentCleared)} sub={`${clearedPct.toFixed(1)}%`} pct={clearedPct}/>
          </div>
          <div style={{ background:"#151c2e", borderRadius:8, padding:"8px 12px" }}>
            <div style={{ fontSize:9, fontWeight:700, textTransform:"uppercase", color:"#eab308", letterSpacing:"0.1em", marginBottom:6 }}>Schedule Breakdown</div>
            <Row label="Gross Sched Goal"  val={fmt(grossSchedGoal)}/>
            <Row label="Rollover"          val={fmt(rollover)}/>
            <Row label="Same-Month Sched Goal" val={fmt(sameMonthSched)}/>
          </div>
          <div style={{ background:"#151c2e", borderRadius:8, padding:"8px 12px", flex:1 }}>
            <div style={{ fontSize:9, fontWeight:700, textTransform:"uppercase", color:"#eab308", letterSpacing:"0.1em", marginBottom:6 }}>Scheduling Pace</div>
            <Row label="Original Daily Target" val={fmt(dailySchedOrig)}/>
            <Row label="Adj. Daily Needed"     val={fmt(adjDailyNeeded)}/>
            <Row label="Days Remaining"        val={schedDaysLeft}/>
            <Row label="Est. Total Cleared"    val={fmt(estTotalCleared)} highlight={estTotalCleared >= clearedGoal ? "#34d399" : "#fmbf24"}/>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── YESTERDAY STATS UPLOADER ────────────────────────────────────────────────
function YesterdayStatsUploader({ onSaved }) {
  const [img,       setImg]       = useState("");
  const [extracting,setExtracting]= useState(false);
  const [preview,   setPreview]   = useState(null);
  const [saving,    setSaving]    = useState(false);
  const [saved,     setSaved]     = useState(false);
  const pasteRef = useRef(null);

  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type.startsWith("image/")) {
        const blob = item.getAsFile();
        const reader = new FileReader();
        reader.onload = (ev) => setImg(ev.target.result);
        reader.readAsDataURL(blob);
        e.preventDefault();
        break;
      }
    }
  };

  const handleExtract = async () => {
    if (!img) return;
    setExtracting(true);
    setPreview(null);
    try {
      const res = await fetch(SCRIPT_URL, {
        method: "POST",
        headers: { "Content-Type": "text/plain" },
        body: JSON.stringify({
          action: "extractYesterdayStats",
          imageBase64: img.split(",")[1],
          mediaType: img.split(";")[0].split(":")[1] || "image/png",
        })
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Extraction failed");
      const parsed = JSON.parse(data.stats);
      setPreview(parsed);
    } catch(e) {
      alert("Extract error: " + e.message);
    }
    setExtracting(false);
  };

  const handleSave = async () => {
    if (!preview) return;
    setSaving(true);
    const result = await saveYesterdayStats(preview);
    if (result.success) { setSaved(true); if (onSaved) onSaved(); }
    else alert("Failed to save. Check Apps Script.");
    setSaving(false);
  };

  return (
    <div style={{ background:"#1e2840", border:"1px solid #2a3148", borderRadius:16, padding:20, display:"flex", flexDirection:"column", gap:14 }}>
      <div style={{ fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.12em", color:"#34d399" }}>📊 Upload Yesterday's Stats</div>

      {/* Paste area */}
      <div ref={pasteRef} onPaste={handlePaste} tabIndex={0} onClick={()=>pasteRef.current?.focus()}
        style={{ background:"#151c2e", border:`2px dashed ${img?"#34d399":"#2a3148"}`, borderRadius:8, padding:img?8:20, minHeight:80, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", outline:"none" }}>
        {img ? (
          <div style={{ position:"relative", width:"100%" }}>
            <img src={img} alt="stats" style={{ maxWidth:"100%", maxHeight:200, borderRadius:6, display:"block", margin:"0 auto" }}/>
            <button onClick={e=>{e.stopPropagation();setImg("");setPreview(null);setSaved(false);}}
              style={{ position:"absolute", top:4, right:4, background:"rgba(0,0,0,0.7)", border:"none", color:"#f87171", borderRadius:4, padding:"2px 6px", cursor:"pointer", fontSize:12 }}>✕</button>
          </div>
        ) : (
          <div style={{ textAlign:"center", color:"#475569", fontSize:13 }}>
            <div style={{ fontSize:24, marginBottom:6 }}>📋</div>
            <div>Click here, then <strong style={{ color:"#94a3b8" }}>Ctrl+V</strong> to paste your stats screenshot</div>
          </div>
        )}
      </div>

      {img && !preview && (
        <button onClick={handleExtract} disabled={extracting}
          style={{ background:"#34d399", color:"#000", border:"none", borderRadius:8, padding:"8px 20px", fontWeight:700, fontSize:13, cursor:extracting?"not-allowed":"pointer", opacity:extracting?0.7:1 }}>
          {extracting ? "🤖 Extracting stats..." : "🤖 Extract Stats with AI"}
        </button>
      )}

      {/* Preview table */}
      {preview && (
        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
          <div style={{ fontSize:11, fontWeight:700, color:"#64748b", textTransform:"uppercase" }}>Preview — confirm before saving</div>
          <div style={{ border:"1px solid #2a3148", borderRadius:8, overflow:"hidden" }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
              <thead>
                <tr style={{ background:"#151c2e" }}>
                  {["Team","Signed","Deals/Agt","Inbound/Agent","% Same Day","% Inb Same Day","No Deal","No Deal %"].map(h=>(
                    <th key={h} style={{ padding:"6px 10px", textAlign:"left", fontSize:10, fontWeight:700, color:"#475569", textTransform:"uppercase" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.map((row,i)=>{
                  const dpa = parseFloat(String(row.dealsPerAgent).replace(/[^0-9.]/g,""));
                  const sds = parseFloat(String(row.sameDaySigned).replace(/[^0-9.]/g,""));
                  const ndp = parseFloat(String(row.noDealPct).replace(/[^0-9.]/g,""));
                  const dpaColor = isNaN(dpa)?"#e2e8f0":dpa>=1.5?"#34d399":dpa>=1.0?"#fbbf24":"#f87171";
                  const sdsColor = isNaN(sds)?"#e2e8f0":sds>=13?"#34d399":sds>=10?"#fbbf24":"#f87171";
                  const ndpColor = isNaN(ndp)?"#e2e8f0":ndp>20?"#f87171":ndp>=16?"#fbbf24":"#34d399";
                  return (
                  <tr key={i} style={{ borderTop:"1px solid #1a2035" }}>
                    <td style={{ padding:"6px 10px", color:"#e2e8f0", fontWeight:600 }}>{row.team}</td>
                    <td style={{ padding:"6px 10px", color:"#34d399", fontWeight:600 }}>{row.signed}</td>
                    <td style={{ padding:"6px 10px", color:dpaColor, fontWeight:600 }}>{row.dealsPerAgent}</td>
                    <td style={{ padding:"6px 10px", color:"#94a3b8" }}>{row.inboundCallsPerAgent}</td>
                    <td style={{ padding:"6px 10px", color:sdsColor, fontWeight:600 }}>{row.sameDaySigned}</td>
                    <td style={{ padding:"6px 10px", color:"#00d4ff" }}>{row.inboundSameDay}</td>
                    <td style={{ padding:"6px 10px", color:ndpColor, fontWeight:600 }}>{row.noDeals}</td>
                    <td style={{ padding:"6px 10px", color:ndpColor, fontWeight:600 }}>{row.noDealPct}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ display:"flex", gap:8 }}>
            <button onClick={handleSave} disabled={saving||saved}
              style={{ background:saved?"#22c55e":"#00d4ff", color:"#000", border:"none", borderRadius:8, padding:"8px 20px", fontWeight:700, fontSize:13, cursor:saving||saved?"not-allowed":"pointer" }}>
              {saving?"Saving...":saved?"✅ Saved to Sheet!":"💾 Save to Morning Prep"}
            </button>
            <button onClick={()=>{setPreview(null);}}
              style={{ background:"transparent", color:"#64748b", border:"1px solid #2a3148", borderRadius:8, padding:"8px 14px", fontSize:13, cursor:"pointer" }}>
              Re-extract
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


// ─── REVIEWS STATS WIDGET ────────────────────────────────────────────────────
function ReviewsStats({ callReviewTasks, oneOnOneTasks, onClick }) {
  const crDone  = (callReviewTasks||[]).filter(t=>t.Task_Status==="Completed").length;
  const crPend  = (callReviewTasks||[]).filter(t=>t.Task_Status==="Pending Review").length;
  const crTotal = (callReviewTasks||[]).length;
  const ooDone  = 0;
  const ooPend  = 0;
  const ooTotal = 0;
  return (
    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }} onClick={onClick}>
      {[
        {label:"Call Reviews",   done:crDone, pend:crPend, total:crTotal, color:"#a78bfa"},
        
      ].map(s=>(
        <div key={s.label} style={{ background:"rgba(0,0,0,0.2)", borderRadius:10, padding:"10px 14px", cursor:onClick?"pointer":"default" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
            <div style={{ fontSize:10, fontWeight:700, textTransform:"uppercase", color:s.color }}>{s.label}</div>
            <div style={{ fontSize:11, fontWeight:700, color:s.total>0?( (s.done/s.total)>=1?"#34d399":(s.done/s.total)>=0.5?"#fbbf24":"#f87171"):"#475569" }}>
              {s.total>0?Math.round(s.done/s.total*100):0}%
            </div>
          </div>
          <div style={{ display:"flex", gap:12, marginBottom:6 }}>
            {[{l:"Done",v:s.done,c:"#34d399"},{l:"Pend",v:s.pend,c:"#00d4ff"},{l:"Open",v:s.total-s.done-s.pend,c:"#f87171"}].map(x=>(
              <div key={x.l} style={{ textAlign:"center" }}>
                <div style={{ fontSize:16, fontWeight:900, color:x.c }}>{x.v}</div>
                <div style={{ fontSize:9, color:"#475569" }}>{x.l}</div>
              </div>
            ))}
          </div>
          <div style={{ background:"rgba(0,0,0,0.3)", borderRadius:999, height:4, overflow:"hidden" }}>
            <div style={{ width:`${s.total>0?(s.done/s.total*100):0}%`, height:"100%", background:"#34d399", borderRadius:999 }}/>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── REVIEWS TABLE ───────────────────────────────────────────────────────────
function ReviewsTable({ tasks, userEmail, userName, isDirector, onRefresh }) {
  const [filter, setFilter] = useState("Open");
  const [localTasks, setLocalTasks] = useState(tasks);

  useEffect(() => { setLocalTasks(tasks); }, [tasks]);

  const crTasks = localTasks.filter(t => t.Task_Type === "CALL_REVIEW");
  const ooTasks = [];
  const crDone  = crTasks.filter(t => t.Task_Status === "Completed").length;
  const ooDone  = ooTasks.filter(t => t.Task_Status === "Completed").length;
  const crPend  = crTasks.filter(t => t.Task_Status === "Pending Review").length;
  const ooPend  = ooTasks.filter(t => t.Task_Status === "Pending Review").length;

  const handleUpdated = (rowIndex, newStatus, newNotes, sheetName) => {
    setLocalTasks(prev => prev.map(t =>
      (t._rowIndex === rowIndex && (t._sheetName||"") === sheetName)
        ? {...t, Task_Status:newStatus, Completion_Notes:newNotes} : t
    ));
  };

  const applyFilter = (list) => filter === "All" ? list : list.filter(t => t.Task_Status === filter);
  const crFiltered = applyFilter(crTasks.map(t=>({...t,_sheetName:"CALL_REVIEW_TASKS"})));
  const ooFiltered = applyFilter(ooTasks.map(t=>({...t,_sheetName:"ONE_ON_ONE_TASKS"})));

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:16 }}>

      {/* Progress summary */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr", gap:12 }}>
        {[
          {label:"Call Reviews", done:crDone, total:crTasks.length, pend:crPend, color:"#a78bfa"},
        ].map(s=>(
          <div key={s.label} style={{ background:"#1e2840", border:"1px solid #2a3148", borderRadius:12, padding:"12px 16px" }}>
            <div style={{ fontSize:11, fontWeight:700, textTransform:"uppercase", color:s.color, marginBottom:8 }}>{s.label}</div>
            <div style={{ display:"flex", gap:16, alignItems:"center" }}>
              <div style={{ textAlign:"center" }}>
                <div style={{ fontSize:22, fontWeight:900, color:"#34d399" }}>{s.done}</div>
                <div style={{ fontSize:10, color:"#475569" }}>Done</div>
              </div>
              <div style={{ textAlign:"center" }}>
                <div style={{ fontSize:22, fontWeight:900, color:"#00d4ff" }}>{s.pend}</div>
                <div style={{ fontSize:10, color:"#475569" }}>Pending</div>
              </div>
              <div style={{ textAlign:"center" }}>
                <div style={{ fontSize:22, fontWeight:900, color:"#f87171" }}>{s.total - s.done - s.pend}</div>
                <div style={{ fontSize:10, color:"#475569" }}>Open</div>
              </div>
              <div style={{ flex:1 }}>
                <div style={{ background:"#151c2e", borderRadius:999, height:6, overflow:"hidden" }}>
                  <div style={{ width:`${s.total>0?(s.done/s.total*100):0}%`, height:"100%", background:"#34d399", borderRadius:999 }}/>
                </div>
                <div style={{ fontSize:10, color:"#64748b", marginTop:3, textAlign:"right" }}>{s.total>0?Math.round(s.done/s.total*100):0}%</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
        {["Open","Pending Review","Completed","All"].map(f=>(
          <button key={f} onClick={()=>setFilter(f)}
            style={{ background:filter===f?"rgba(0,212,255,0.1)":"#1e2840", color:filter===f?"#00d4ff":"#64748b", border:`1px solid ${filter===f?"rgba(0,212,255,0.3)":"#2a3148"}`, borderRadius:8, padding:"4px 12px", fontSize:12, fontWeight:500, cursor:"pointer" }}>
            {f}
          </button>
        ))}
      </div>

      {/* Call Reviews */}
      {crFiltered.length > 0 && (
        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
          <div style={{ fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.12em", color:"#a78bfa" }}>
            📞 Call Reviews — {crDone}/{crTasks.length} completed
          </div>
          <div style={{ border:"1px solid #2a3148", borderRadius:12, overflow:"hidden" }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
              <thead>
                <tr style={{ background:"#151c2e", borderBottom:"1px solid #2a3148" }}>
                  {["Agent","Type","Priority","Status","Proof",""].map(h=>(
                    <th key={h} style={{ textAlign:"left", padding:"8px 14px", fontSize:10, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.05em", color:"#475569" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {crFiltered.map((t,i)=>(
                  <TaskRow key={`cr-${t._rowIndex||i}`} task={t} typeCol="Task_Type"
                    sheetName="CALL_REVIEW_TASKS" userEmail={userEmail} userName={userName}
                    onUpdated={(ri,ns,nn)=>handleUpdated(ri,ns,nn,"CALL_REVIEW_TASKS")}
                    isDirector={isDirector}/>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 1-on-1s */}
      {ooFiltered.length > 0 && (
        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
          <div style={{ fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.12em", color:"#00d4ff" }}>
            🤝 1-on-1 Sessions — {ooDone}/{ooTasks.length} completed
          </div>
          <div style={{ border:"1px solid #2a3148", borderRadius:12, overflow:"hidden" }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
              <thead>
                <tr style={{ background:"#151c2e", borderBottom:"1px solid #2a3148" }}>
                  {["Agent","Type","Priority","Status","Proof",""].map(h=>(
                    <th key={h} style={{ textAlign:"left", padding:"8px 14px", fontSize:10, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.05em", color:"#475569" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ooFiltered.map((t,i)=>(
                  <TaskRow key={`oo-${t._rowIndex||i}`} task={t} typeCol="Task_Type"
                    sheetName="ONE_ON_ONE_TASKS" userEmail={userEmail} userName={userName}
                    onUpdated={(ri,ns,nn)=>handleUpdated(ri,ns,nn,"ONE_ON_ONE_TASKS")}
                    isDirector={isDirector}/>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {crFiltered.length === 0 && ooFiltered.length === 0 && (
        <div style={{ textAlign:"center", padding:48, color:"#475569" }}>No tasks match this filter.</div>
      )}
    </div>
  );
}

// ─── PUSH TASK WIDGET ────────────────────────────────────────────────────────
function PushTaskWidget({ teamMaster, onPushed }) {
  const [taskType,   setTaskType]   = useState("");
  const [taskAction, setTaskAction] = useState("");
  const [selected,   setSelected]   = useState({});
  const [pushing,    setPushing]    = useState(false);
  const [pushed,     setPushed]     = useState(false);

  const managers = teamMaster
    .filter(r => (r["Team Status"]||r.Team_Status||"Active") === "Active" && r.Manager && r["Manager Email"])
    .map(r => ({ name: r.Manager, team: r.Team, email: r["Manager Email"] }))
    .filter((m,i,arr) => arr.findIndex(x=>x.email===m.email) === i) // dedupe
    .sort((a,b) => a.name.localeCompare(b.name));

  const allSelected = managers.length > 0 && managers.every(m => selected[m.email]);
  const selectedManagers = managers.filter(m => selected[m.email]);

  const toggleAll = () => {
    if (allSelected) setSelected({});
    else { const s = {}; managers.forEach(m => { s[m.email] = true; }); setSelected(s); }
  };

  const handlePush = async () => {
    if (!taskType.trim() || !taskAction.trim() || selectedManagers.length === 0) return;
    setPushing(true);
    const ok = await pushTask(taskType.trim(), taskAction.trim(), selectedManagers);
    if (ok) {
      setPushed(true);
      setTaskType(""); setTaskAction(""); setSelected({});
      setTimeout(() => { setPushed(false); if (onPushed) onPushed(); }, 3000);
    }
    setPushing(false);
  };

  return (
    <div style={{ background:"rgba(234,179,8,0.04)", border:"1px solid rgba(234,179,8,0.2)", borderRadius:12, padding:"14px 20px", display:"flex", alignItems:"center", gap:12, flexWrap:"wrap" }}>
      <span style={{ fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.1em", color:"#eab308", flexShrink:0 }}>⚡ Push Task</span>
      <input value={taskType} onChange={e=>setTaskType(e.target.value)} placeholder="Type (e.g. Script Update)"
        style={{ background:"#1e2840", border:"1px solid #2a3148", borderRadius:8, padding:"8px 12px", color:"#e2e8f0", fontSize:13, outline:"none", width:180 }}/>
      <input value={taskAction} onChange={e=>setTaskAction(e.target.value)} placeholder="Action required..."
        style={{ background:"#1e2840", border:"1px solid #2a3148", borderRadius:8, padding:"8px 12px", color:"#e2e8f0", fontSize:13, outline:"none", flex:1, minWidth:200 }}/>
      <div style={{ display:"flex", gap:6, flexWrap:"wrap", alignItems:"center" }}>
        <button onClick={toggleAll} style={{ background:"none", border:"1px solid #2a3148", borderRadius:6, padding:"5px 10px", fontSize:12, color:"#64748b", cursor:"pointer" }}>
          {allSelected ? "None" : "All"}
        </button>
        {managers.map(m => (
          <button key={m.email} onClick={()=>setSelected(p=>({...p,[m.email]:!p[m.email]}))}
            style={{ background:selected[m.email]?"rgba(234,179,8,0.15)":"#1e2840", border:`1px solid ${selected[m.email]?"rgba(234,179,8,0.4)":"#2a3148"}`, borderRadius:6, padding:"5px 10px", fontSize:12, fontWeight:selected[m.email]?700:400, color:selected[m.email]?"#eab308":"#64748b", cursor:"pointer" }}>
            {m.name.split(" ")[0]}
          </button>
        ))}
      </div>
      <button onClick={handlePush} disabled={pushing||!taskType.trim()||!taskAction.trim()||selectedManagers.length===0}
        style={{ background:pushed?"#22c55e":"#eab308", color:"#000", border:"none", borderRadius:8, padding:"8px 20px", fontWeight:700, fontSize:13, cursor:"pointer", flexShrink:0, opacity:pushing||!taskType.trim()||!taskAction.trim()||selectedManagers.length===0?0.5:1 }}>
        {pushing?"...":pushed?"✓ Sent!":`⚡ Send${selectedManagers.length>0?` (${selectedManagers.length})`:""}`}
      </button>
    </div>
  );
}

// ─── CALL REVIEWS ARCHIVE ─────────────────────────────────────────────────────
function CallReviewsArchive({ callReviews, user, isDirector, teamMaster }) {
  const [selectedAgent, setSelectedAgent] = useState(null);

  // Filter reviews by manager's team unless director
  const myTeam = teamMaster.find(r => (r["Manager Email"]||"").toLowerCase() === (user?.email||"").toLowerCase());
  const myTeamName = myTeam?.Team || "";

  const visibleReviews = isDirector
    ? callReviews
    : callReviews.filter(r => r.Team === myTeamName);

  // Group by agent
  const agentMap = {};
  visibleReviews.forEach(r => {
    const key = r.Agent || "Unknown";
    if (!agentMap[key]) agentMap[key] = { agent: key, team: r.Team, manager: r.Manager, reviews: [] };
    agentMap[key].reviews.push(r);
  });
  const agents = Object.values(agentMap).sort((a,b) => a.agent.localeCompare(b.agent));

  if (agents.length === 0) return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", padding:64, gap:12 }}>
      <div style={{ fontSize:32 }}>📞</div>
      <div style={{ color:"#475569", fontSize:14 }}>No call reviews on file yet.</div>
    </div>
  );

  if (selectedAgent) {
    const agentData = agentMap[selectedAgent];
    const sorted = [...agentData.reviews].sort((a,b) => new Date(b.Timestamp) - new Date(a.Timestamp));
    return (
      <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <button onClick={()=>setSelectedAgent(null)} style={{ background:"none", border:"1px solid #2a3148", borderRadius:8, padding:"6px 12px", color:"#64748b", fontSize:13, cursor:"pointer" }}>← Back</button>
          <span style={{ fontWeight:700, fontSize:16, color:"#e2e8f0" }}>{selectedAgent}</span>
          <span style={{ fontSize:12, color:"#64748b" }}>{agentData.team} · {sorted.length} review{sorted.length!==1?"s":""}</span>
        </div>
        {sorted.map((r,i) => (
          <div key={i} style={{ background:"#1e2840", border:"1px solid #2a3148", borderRadius:16, padding:20 }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16 }}>
              <div style={{ fontSize:12, color:"#64748b" }}>
                {r.Timestamp ? new Date(r.Timestamp).toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"}) : "—"}
              </div>
              <div style={{ fontSize:12, color:"#64748b" }}>By {r.Manager} · Approved by {r.Approved_By||"—"}</div>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
              {[
                {label:"File Number(s)", value:r.File_Numbers},
                {label:"Date of Call",   value:r.Date_of_Call},
              ].map(f=>(
                <div key={f.label} style={{ background:"#151c2e", borderRadius:8, padding:"10px 14px" }}>
                  <div style={{ fontSize:10, fontWeight:700, textTransform:"uppercase", color:"#475569", marginBottom:4 }}>{f.label}</div>
                  <div style={{ fontSize:13, color:"#e2e8f0" }}>{f.value||"—"}</div>
                </div>
              ))}
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:10, marginTop:12 }}>
              {[
                {label:"✅ Strengths / Best Practices", value:r.Strengths, color:"#34d399"},
                {label:"📉 Areas of Improvement",       value:r.Areas_of_Improvement, color:"#fbbf24"},
                {label:"📋 Plan of Action",             value:r.Plan_of_Action, color:"#a78bfa"},
              ].map(f=>(
                <div key={f.label} style={{ background:"#151c2e", borderRadius:8, padding:"10px 14px" }}>
                  <div style={{ fontSize:10, fontWeight:700, textTransform:"uppercase", color:f.color, marginBottom:6 }}>{f.label}</div>
                  <div style={{ fontSize:13, color:"#e2e8f0", whiteSpace:"pre-wrap", lineHeight:1.6 }}>{f.value||"—"}</div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
      <div style={{ fontSize:13, color:"#64748b", marginBottom:4 }}>{agents.length} agent{agents.length!==1?"s":""} with call reviews on file</div>
      {agents.map((a,i) => (
        <div key={i} onClick={()=>setSelectedAgent(a.agent)}
          style={{ background:"#1e2840", border:"1px solid #2a3148", borderRadius:12, padding:"14px 20px", display:"flex", alignItems:"center", justifyContent:"space-between", cursor:"pointer" }}
          onMouseEnter={e=>e.currentTarget.style.borderColor="#8b5cf6"}
          onMouseLeave={e=>e.currentTarget.style.borderColor="#2a3148"}
        >
          <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
            <div style={{ fontWeight:600, color:"#e2e8f0", fontSize:14 }}>{a.agent}</div>
            <div style={{ fontSize:12, color:"#64748b" }}>{a.team}{isDirector ? ` · ${a.manager}` : ""}</div>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:12 }}>
            <div style={{ background:"rgba(139,92,246,0.15)", border:"1px solid rgba(139,92,246,0.3)", borderRadius:8, padding:"4px 12px", fontSize:12, fontWeight:700, color:"#a78bfa" }}>
              {a.reviews.length} review{a.reviews.length!==1?"s":""}
            </div>
            <span style={{ color:"#475569", fontSize:14 }}>›</span>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── BROADCAST FEED (chat style) ─────────────────────────────────────────────
function BroadcastFeed({ broadcast, userName, userEmail, isDirector, onReply, narrow }) {
  const [replyText,   setReplyText]   = useState("");
  const [sending,     setSending]     = useState(false);
  const [showEmojis,  setShowEmojis]  = useState(false);
  const [chatImg,     setChatImg]     = useState("");
  const feedRef = useRef(null);

  const handleChatPaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type.startsWith("image/")) {
        const blob = item.getAsFile();
        const canvas = document.createElement("canvas");
        const img = new Image();
        img.onload = () => {
          const maxW = 800;
          const scale = img.width > maxW ? maxW / img.width : 1;
          canvas.width = img.width * scale;
          canvas.height = img.height * scale;
          canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
          setChatImg(canvas.toDataURL("image/jpeg", 0.7));
        };
        img.src = URL.createObjectURL(blob);
        e.preventDefault();
        break;
      }
    }
  };

  const EMOJIS = [
    "😊","😂","😅","🤔","😎","🙏","🥳","😤","😬","🫡",
    "👍","👎","👏","🙌","💪","🤝","🫶","☝️","✌️","🤞",
    "🔥","✅","❌","⚠️","💡","📢","🎯","🚀","⏰","🏆",
    "📊","📋","💰","🎉","⭐","💯","🆗","🆙","❗","❓",
    "😴","🤯","🫠","😩","💀","🤦","🙄","😮","🫢","🤐",
  ];

  useEffect(() => {
    if (feedRef.current) feedRef.current.scrollTop = feedRef.current.scrollHeight;
  }, [broadcast]);

  const handleSend = async () => {
    if (!replyText.trim() && !chatImg) return;
    setSending(true);
    const msg = chatImg ? (replyText.trim() ? replyText.trim() + "\n[img]" + chatImg : "[img]" + chatImg) : replyText.trim();
    const result = await postBroadcast(msg, userName || userEmail, "", isDirector ? "director" : "manager");
    if (result.success) { setReplyText(""); setChatImg(""); if (onReply) onReply(); }
    setSending(false);
  };

  return (
    <div style={{ background:"linear-gradient(135deg, #1a1030, #1e2840)", border:"1px solid rgba(139,92,246,0.4)", borderRadius:16, padding:20, display:"flex", flexDirection:"column", gap:12, height: narrow ? "100%" : "auto", boxSizing:"border-box" }}>
      <div style={{ display:"flex", alignItems:"center", gap:8 }}>
        <span style={{ fontSize:18 }}>📢</span>
        <span style={{ fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.1em", color:"#a78bfa" }}>Team Chat</span>
        <span style={{ fontSize:11, color:"#475569", marginLeft:"auto" }}>{broadcast.length} message{broadcast.length!==1?"s":""} today</span>
      </div>

      {/* Scrollable feed */}
      <div ref={feedRef} style={{ display:"flex", flexDirection:"column", gap:8, maxHeight: narrow ? "none" : 220, minHeight: narrow ? 200 : "auto", flex: narrow ? 1 : "none", overflowY:"auto", paddingRight:4 }}>
        {broadcast.length === 0 && (
          <div style={{ textAlign:"center", color:"#475569", fontSize:13, padding:"16px 0" }}>📢 No messages yet today</div>
        )}
        {broadcast.map((msg, i) => {
          const isMe = (msg.Posted_By === userName || msg.Posted_By === userEmail);
          const isDir = msg.Author_Type === "director" || (!msg.Author_Type && msg.Posted_By?.toLowerCase().includes("chris"));
          return (
            <div key={i} style={{ display:"flex", flexDirection:"column", gap:2, alignItems: isMe ? "flex-end" : "flex-start" }}>
              <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                <span style={{ fontSize:10, fontWeight:700, color:isDir?"#a78bfa":"#00d4ff" }}>{msg.Posted_By}</span>
                <span style={{ fontSize:10, color:"#475569" }}>{msg.Timestamp}</span>
              </div>
              <div style={{ background: isDir?"rgba(139,92,246,0.15)":"rgba(0,212,255,0.1)", border:`1px solid ${isDir?"rgba(139,92,246,0.3)":"rgba(0,212,255,0.2)"}`, borderRadius:10, padding:"8px 12px", maxWidth:"85%", fontSize:13, color:"#e2e8f0", lineHeight:1.5 }}>
                {msg.Message && !msg.Message.startsWith("[img]") && (
                  <div style={{ whiteSpace:"pre-wrap" }}>{msg.Message.replace(/\[img\].*/s,"")}</div>
                )}
                {msg.Message && msg.Message.includes("[img]") && (
                  <img src={msg.Message.split("[img]")[1]} alt="shared" style={{ maxWidth:"100%", maxHeight:200, borderRadius:6, display:"block", marginTop: msg.Message.startsWith("[img]")?0:8 }}/>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Reply box */}
      <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
        {showEmojis && (
          <div style={{ background:"#1e2840", border:"1px solid #2a3148", borderRadius:10, padding:10, display:"flex", flexWrap:"wrap", gap:6 }}>
            {EMOJIS.map(emoji => (
              <button key={emoji} onClick={()=>{ setReplyText(t=>t+emoji); setShowEmojis(false); }}
                style={{ background:"none", border:"none", fontSize:20, cursor:"pointer", padding:"2px 4px", borderRadius:6, lineHeight:1 }}
                onMouseEnter={e=>e.currentTarget.style.background="#2a3148"}
                onMouseLeave={e=>e.currentTarget.style.background="none"}
              >{emoji}</button>
            ))}
          </div>
        )}
        <div style={{ display:"flex", gap:8 }}>
          <button onClick={()=>setShowEmojis(v=>!v)}
            style={{ background:"#1e2840", border:"1px solid #2a3148", borderRadius:8, padding:"8px 10px", fontSize:16, cursor:"pointer", flexShrink:0 }}>
            😊
          </button>
          <input
            value={replyText}
            onChange={e=>setReplyText(e.target.value)}
            onKeyDown={e=>{ if(e.key==="Enter"&&!e.shiftKey){ e.preventDefault(); handleSend(); }}}
            placeholder={isDirector ? "Send a message..." : "Send a message..."}
            style={{ flex:1, background:"#1e2840", border:"1px solid rgba(139,92,246,0.3)", borderRadius:8, padding:"8px 12px", color:"#e2e8f0", fontSize:13, outline:"none" }}
          />
          <button onClick={handleSend} disabled={sending||!replyText.trim()}
            style={{ background:"#8b5cf6", color:"#fff", border:"none", borderRadius:8, padding:"8px 16px", fontWeight:700, fontSize:13, cursor:sending||!replyText.trim()?"not-allowed":"pointer", opacity:sending||!replyText.trim()?0.6:1 }}>
            {sending?"...":"Send"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── BROADCAST PANEL ─────────────────────────────────────────────────────────
function BroadcastPanel({ data, userEmail, userName, onSent }) {
  const [message,  setMessage]  = useState("");
  const [sending,  setSending]  = useState(false);
  const [sent,     setSent]     = useState(false);

  const latest = data?.broadcast?.length > 0 ? data.broadcast[data.broadcast.length - 1] : null;

  const handleSend = async () => {
    if (!message.trim()) return;
    setSending(true);
    const now = new Date().toLocaleString("en-US", { month:"short", day:"numeric", hour:"numeric", minute:"2-digit" });
    const result = await postBroadcast(message.trim(), userName || userEmail);
    if (result.success) { setMessage(""); setSent(true); setTimeout(()=>setSent(false), 3000); if (onSent) onSent(); }
    setSending(false);
  };

  return (
    <div style={{ background:"#1a1030", border:"1px solid rgba(139,92,246,0.4)", borderRadius:16, padding:20, display:"flex", flexDirection:"column", gap:12 }}>
      <div style={{ display:"flex", alignItems:"center", gap:8 }}>
        <span style={{ fontSize:18 }}>📢</span>
        <span style={{ fontSize:13, fontWeight:700, color:"#a78bfa", textTransform:"uppercase", letterSpacing:"0.1em" }}>Team Chat</span>
        {latest && <span style={{ fontSize:11, color:"#475569", marginLeft:"auto" }}>Last sent: {latest.Timestamp}</span>}
      </div>
      {data?.broadcast && data.broadcast.length > 0 && (
        <div style={{ display:"flex", flexDirection:"column", gap:8, maxHeight:150, overflowY:"auto" }}>
          {data.broadcast.map((msg, i) => (
            <div key={i} style={{ background:"#151c2e", borderRadius:8, padding:"8px 12px", borderLeft:"3px solid rgba(139,92,246,0.4)" }}>
              <div style={{ fontSize:11, color:"#475569", marginBottom:3 }}>{msg.Timestamp}</div>
              <div style={{ fontSize:13, color:"#94a3b8" }}>{msg.Message}</div>
            </div>
          ))}
        </div>
      )}
      <textarea
        value={message}
        onChange={e=>setMessage(e.target.value)}
        placeholder="Type your broadcast message to all managers..."
        style={{ width:"100%", background:"#1e2840", border:"1px solid rgba(139,92,246,0.3)", borderRadius:8, padding:12, color:"#e2e8f0", fontSize:13, resize:"vertical", minHeight:80, outline:"none", fontFamily:"inherit", boxSizing:"border-box" }}
      />
      <div style={{ display:"flex", alignItems:"center", gap:12 }}>
        <button onClick={handleSend} disabled={sending||!message.trim()} style={{ background:"#8b5cf6", color:"#fff", border:"none", borderRadius:8, padding:"8px 20px", fontWeight:700, fontSize:13, cursor:sending||!message.trim()?"not-allowed":"pointer", opacity:sending||!message.trim()?0.6:1 }}>
          {sending?"Sending...":sent?"✓ Sent!":"📢 Send Broadcast"}
        </button>
        <span style={{ fontSize:12, color:"#475569" }}>All managers and director can see this</span>
      </div>
    </div>
  );
}

// ─── DIRECTOR TASK ROW (correct columns + expand + checkbox) ─────────────────
function DirectorTaskRow({ task, typeCol, sheetName, source, userEmail, userName, showCheckbox, checked, onCheck }) {
  const [expanded,    setExpanded]    = useState(false);
  const [notes,       setNotes]       = useState(task.Completion_Notes || "");
  const [submitting,  setSubmitting]  = useState(false);
  const [localStatus, setLocalStatus] = useState(task.Task_Status);
  const [localProof,  setLocalProof]  = useState(task.Proof_File || "");

  const isDone    = localStatus === "Completed";
  const isPending = localStatus === "Pending Review";
  const tType     = task[typeCol];
  const colCount  = showCheckbox ? 10 : 9;
  const isManualTask = tType && !["DONUT_CHAT_GROUP","ZERO_CONVERSION","LOW_CONVERSION","STREAK_2","STREAK_3","STREAK_4","STREAK_5","FREQ_2_OF_3","FREQ_3_OF_5","SATURDAY_SIGNUPS_REVIEW","MORNING_MESSAGE_PREP","AGENT_TIME_DETAIL","FOLLOWUP_CALLS","WEEKLY_DOUBLE_MISS","WEEKLY_LOW_PRODUCTION","WEEKLY_LOW_CONVERSION","MTD_TRIPLE_MISS","MTD_PRODUCTION_CONVERSION","MTD_PRODUCTION_CANCEL","MTD_CONVERSION_CANCEL","MTD_LOW_PRODUCTION","MTD_LOW_CONVERSION","MTD_HIGH_CANCEL","GOALS_EMAIL","CALL_REVIEW","ONE_ON_ONE_1","ONE_ON_ONE_2","DAILY_SPOT_CHECK","COMPLIANCE_CHECK_1","COMPLIANCE_CHECK_2","COMPLIANCE_CHECK_3","COMPLIANCE_CHECK_4"].includes(tType);

  const handleApprove = async () => {
    setSubmitting(true);
    const directorNote = notes.trim() ? "[Director]: " + notes.trim() : "";
    const payload = { status:"Completed", completedBy: userEmail, completionNotes: directorNote || task.Completion_Notes };

    // Parse form data for archiving
    const isCallReviewTask = tType === "CALL_REVIEW" || task.Task_Type === "CALL_REVIEW";
    const isOneOnOneTask   = tType === "ONE_ON_ONE_1" || tType === "ONE_ON_ONE_2" || task.Task_Type === "ONE_ON_ONE_1" || task.Task_Type === "ONE_ON_ONE_2";
    if (isCallReviewTask || isOneOnOneTask) {
      try {
        const parsed = JSON.parse(task.Completion_Notes || "{}");
        if (isCallReviewTask) payload.callReviewData = { ...parsed, agent: task.Agent_Name || task.Agents_List, team: task.Team, manager: task.Manager };
        if (isOneOnOneTask)   payload.oneOnOneData   = { ...parsed, agent: task.Agent_Name || task.Agents_List, team: task.Team, manager: task.Manager };
      } catch(e) {}
    }

    const result = await updateTask(sheetName, task._rowIndex, payload);
    if (result.success) setLocalStatus("Completed");
    setSubmitting(false);
  };

  const handleSendBack = async () => {
    const reason = window.prompt("Note to manager (they will see this in the portal):");
    if (reason === null) return;
    setSubmitting(true);
    const sendBackNote = reason ? "[Director]: " + reason : "[Director]: Task sent back for revision";
    const result = await updateTask(sheetName, task._rowIndex, { status:"Open", completionNotes: sendBackNote, proofData:"", completedBy:"" });
    if (result.success) { setLocalStatus("Open"); setLocalProof(""); setNotes(sendBackNote); }
    setSubmitting(false);
  };

  return (
    <>
      <tr onClick={() => setExpanded(!expanded)} style={{ borderBottom:"1px solid #1a2035", background:isDone?"#0f1a10":isPending?"#1a1030":isManualTask&&!isDone&&!isPending?"rgba(0,212,255,0.06)":"transparent", cursor:"pointer" }}>
        {showCheckbox && (
          <td style={{ padding:"10px 16px" }} onClick={e=>e.stopPropagation()}>
            {isPending && <input type="checkbox" checked={checked} onChange={onCheck} style={{ width:16, height:16, cursor:"pointer", accentColor:"#8b5cf6" }}/>}
          </td>
        )}
        <td style={{ padding:"10px 16px" }}><span style={{ fontSize:11, fontWeight:700, background:"#2a3148", color:"#94a3b8", padding:"2px 6px", borderRadius:4 }}>{source}</span></td>
        <td style={{ padding:"10px 16px", color:"#e2e8f0", fontWeight:500 }}>{task.Manager}</td>
        <td style={{ padding:"10px 16px", color:"#94a3b8" }}>{task.Team}</td>
        <td style={{ padding:"10px 16px", color:"#94a3b8" }}>{friendlyType(tType)}</td>
        <td style={{ padding:"10px 16px", color:"#94a3b8" }}>
          <div>{task.Agent_Name || task.Agents_List || "—"}</div>
          {task.Flag_Reason && <div style={{ fontSize:11, color:"#475569", marginTop:2 }}>{task.Flag_Reason}</div>}
        </td>
        <td style={{ padding:"10px 16px" }}><Bdg cls={priorityBadge(task.Priority)}>{task.Priority}</Bdg></td>
        <td style={{ padding:"10px 16px" }}><Bdg cls={statusBadge(localStatus)}>{localStatus}</Bdg></td>
        <td style={{ padding:"10px 16px" }}><Bdg cls={proofBadge(localProof?"Submitted":task.Proof_Status)}>{localProof?"Submitted":(task.Proof_Status||"Missing")}</Bdg></td>
        <td style={{ padding:"10px 16px", color:"#475569", fontSize:12 }}>{expanded?"▲":"▼"}</td>
      </tr>

      {expanded && (
        <tr style={{ borderBottom:"1px solid #2a3148" }}>
          <td colSpan={colCount} style={{ padding:0 }}>
            <div style={{ background:"#151c2e", padding:20, display:"flex", flexDirection:"column", gap:16 }}>
              {/* Task details */}
              <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:12 }}>
                {[
                  {label:"Task Type",       value:friendlyType(tType)},
                  {label:"Agent",           value:task.Agent_Name||task.Agents_List||"—"},
                  {label:"Required Action", value:task.Required_Action||"—"},
                  {label:"Priority",        value:task.Priority},
                  {label:"Due",             value:task.Due_Time||"EOD"},
                  {label:"Manager",         value:task.Manager||"—"},
                ].map(f=>(
                  <div key={f.label} style={{ background:"#1e2840", borderRadius:8, padding:"10px 12px" }}>
                    <div style={{ fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.08em", color:"#475569", marginBottom:4 }}>{f.label}</div>
                    <div style={{ fontSize:13, color:"#e2e8f0" }}>{f.value}</div>
                  </div>
                ))}
              </div>

              {/* Proof */}
              {localProof && (
                <div>
                  <div style={{ fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.08em", color:"#a78bfa", marginBottom:8 }}>📎 Submitted Proof</div>
                  {localProof.startsWith("https://") ? (
                    <a href={localProof} target="_blank" rel="noreferrer" style={{ display:"inline-flex", alignItems:"center", gap:8, background:"rgba(139,92,246,0.15)", border:"1px solid rgba(139,92,246,0.4)", borderRadius:8, padding:"10px 16px", color:"#a78bfa", fontSize:13, fontWeight:600, textDecoration:"none" }}>
                      🖼 View Screenshot →
                    </a>
                  ) : localProof.startsWith("data:") ? (
                    <img src={localProof} alt="proof" style={{ maxWidth:"100%", maxHeight:400, borderRadius:8, display:"block" }}/>
                  ) : (
                    <div style={{ fontSize:13, color:"#94a3b8", background:"#1e2840", borderRadius:8, padding:12 }}>{localProof}</div>
                  )}
                </div>
              )}

              {/* Submitted form data for call review / 1-on-1 */}
              {(isPending || isDone) && (tType === "CALL_REVIEW" || task.Task_Type === "CALL_REVIEW") && SubmittedCallReview(task.Completion_Notes)}
              {(isPending || isDone) && (tType === "ONE_ON_ONE_1" || tType === "ONE_ON_ONE_2" || task.Task_Type === "ONE_ON_ONE_1" || task.Task_Type === "ONE_ON_ONE_2") && SubmittedOneOnOne(task.Completion_Notes)}

              {/* Director note box — hide for form tasks where notes contains JSON */}
              {!(tType === "CALL_REVIEW" || task.Task_Type === "CALL_REVIEW" || tType === "ONE_ON_ONE_1" || tType === "ONE_ON_ONE_2" || task.Task_Type === "ONE_ON_ONE_1" || task.Task_Type === "ONE_ON_ONE_2") && (
              <div>
                <div style={{ fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.08em", color:"#a78bfa", marginBottom:8 }}>📝 Note to Manager (optional)</div>
                <textarea value={notes} onChange={e=>setNotes(e.target.value)}
                  placeholder="Leave a note for the manager — they'll see it in the portal..."
                  style={{ width:"100%", background:"#1a1030", border:"1px solid rgba(139,92,246,0.3)", borderRadius:8, padding:12, color:"#e2e8f0", fontSize:13, resize:"vertical", minHeight:60, outline:"none", fontFamily:"inherit", boxSizing:"border-box" }}
                />
              </div>
              )}

              {/* Actions */}
              <div style={{ display:"flex", gap:12, alignItems:"center" }}>
                {isPending && (
                  <>
                    <button onClick={handleApprove} disabled={submitting} style={{ background:"#22c55e", color:"#000", border:"none", borderRadius:8, padding:"8px 20px", fontWeight:700, fontSize:13, cursor:submitting?"not-allowed":"pointer" }}>
                      {submitting?"...":"✓ Approve — Mark Complete"}
                    </button>
                    <button onClick={handleSendBack} disabled={submitting} style={{ background:"transparent", color:"#f87171", border:"1px solid #3a1a1a", borderRadius:8, padding:"8px 16px", fontWeight:600, fontSize:13, cursor:"pointer" }}>
                      ↩ Send Back
                    </button>
                  </>
                )}
                {isDone && (
                  <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                    <Bdg cls="bg-green-500/20 text-green-400 border-green-500/40">✅ Completed</Bdg>
                    {task.Completed_By && <span style={{ fontSize:12, color:"#64748b" }}>by {task.Completed_By}</span>}
                    <button onClick={handleSendBack} disabled={submitting} style={{ background:"transparent", color:"#f87171", border:"1px solid #3a1a1a", borderRadius:8, padding:"4px 12px", fontWeight:600, fontSize:12, cursor:"pointer", marginLeft:8 }}>↩ Reopen</button>
                  </div>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ─── DIRECTOR VIEW ────────────────────────────────────────────────────────────
function DirectorView({ data, userEmail, userName, loadData }) {
  const [companyDrilled, setCompanyDrilled] = useState(false);
  const [drillTeam,    setDrillTeam]    = useState(null);
  const [drillSource,  setDrillSource]  = useState(null);
  const [sourceFilter, setSourceFilter] = useState("All");
  const [filter,       setFilter]       = useState("Open");
  const [selected,     setSelected]     = useState({});
  const [bulkApproving,setBulkApproving]= useState(false);
  const [localStatuses,setLocalStatuses]= useState({});

  if (!data) return <div style={{ color:"#64748b", padding:32, textAlign:"center" }}>Loading...</div>;

  const activeTeams = new Set(
    (data.teamMaster||[])
      .filter(r => (r["Team Status"]||r.Team_Status||"Active") === "Active")
      .map(r => r.Team)
      .filter(Boolean)
  );

  const allTasks = [
    ...(data.managerTasks||[]).map(t=>({...t,_source:"DAILY", _typeCol:t.Task_Type, _sheetName:"MANAGER_TASKS"})),
    ...(data.weeklyTasks ||[]).map(t=>({...t,_source:"WEEKLY",_typeCol:t.Weekly_Flag, _sheetName:"WEEKLY_MANAGER_TASKS"})),
    ...(data.mtdTasks    ||[]).map(t=>({...t,_source:"MTD",   _typeCol:t.MTD_Flag,    _sheetName:"MTD_MANAGER_TASKS"})),
    ...(data.prepTasks   ||[]).filter(t=>(t.Task_Type||"")!=="MORNING_MESSAGE_PREP").map(t=>({...t,_source:"DAILY",_typeCol:t.Task_Type,_sheetName:"DAILY_MANAGER_PREP_TASKS"})),
    ...(data.callReviewTasks||[]).map(t=>({...t,_source:"REVIEWS",_typeCol:t.Task_Type,_sheetName:"CALL_REVIEW_TASKS"})),
    ...(data.oneOnOneTasks||[]).map(t=>({...t,_source:"REVIEWS",_typeCol:t.Task_Type,_sheetName:"ONE_ON_ONE_TASKS"})),
  ].filter(t => !t.Team || activeTeams.size === 0 || activeTeams.has(t.Team));

  const teams = [...new Set(allTasks.map(t=>t.Team).filter(Boolean))].sort();
  const resolvedDrillTeam = drillTeam;

  // Stats helper
  const stats = (tasks) => ({
    open:    tasks.filter(t=>t.Task_Status==="Open").length,
    pending: tasks.filter(t=>t.Task_Status==="Pending Review").length,
    done:    tasks.filter(t=>t.Task_Status==="Completed").length,
    total:   tasks.length,
    pct:     tasks.length > 0 ? Math.round((tasks.filter(t=>t.Task_Status==="Completed").length/tasks.length)*100) : 0,
  });

  // Summary stats per team
  const teamStats = teams.map(team => {
    const tt = allTasks.filter(t => t.Team === team);
    return {
      team,
      daily:  stats(tt.filter(t=>t._source==="DAILY")),
      weekly: stats(tt.filter(t=>t._source==="WEEKLY")),
      mtd:    stats(tt.filter(t=>t._source==="MTD")),
      total:  stats(tt),
    };
  });

  // Drill down task list
  const drillTasks = resolvedDrillTeam && drillSource
    ? resolvedDrillTeam === "ALL"
      ? (sourceFilter === "All" ? allTasks : allTasks.filter(t => t._source === sourceFilter))
      : drillSource === "ALL"
        ? (sourceFilter === "All"
            ? allTasks.filter(t => t.Team === resolvedDrillTeam)
            : allTasks.filter(t => t.Team === resolvedDrillTeam && t._source === sourceFilter))
        : allTasks.filter(t => t.Team === resolvedDrillTeam && t._source === drillSource)
    : [];
  const filtered = filter === "All" ? drillTasks : drillTasks.filter(t => (localStatuses[t._rowIndex+t._sheetName] || t.Task_Status) === filter);
  const pendingTasks = drillTasks.filter(t => (localStatuses[t._rowIndex+t._sheetName] || t.Task_Status) === "Pending Review");
  const selectedKeys = Object.keys(selected).filter(k => selected[k]);
  const allPendingSelected = pendingTasks.length > 0 && pendingTasks.every(t => selected[t._rowIndex+t._sheetName]);

  const toggleSelectAll = () => {
    if (allPendingSelected) { setSelected({}); }
    else { const n = {}; pendingTasks.forEach(t => { n[t._rowIndex+t._sheetName] = true; }); setSelected(n); }
  };

  const handleBulkApprove = async () => {
    const toApprove = pendingTasks.filter(t => selected[t._rowIndex+t._sheetName]);
    if (toApprove.length === 0) return;
    setBulkApproving(true);
    await Promise.all(toApprove.map(t => updateTask(t._sheetName, t._rowIndex, { status:"Completed", completedBy: userEmail })));
    const newStatuses = {...localStatuses};
    toApprove.forEach(t => { newStatuses[t._rowIndex+t._sheetName] = "Completed"; });
    setLocalStatuses(newStatuses); setSelected({}); setBulkApproving(false);
  };

  const pctColor = (pct) => pct === 100 ? "#34d399" : pct >= 50 ? "#fbbf24" : "#f87171";

  const companyStats = stats(allTasks);

  return (
    <div style={{ flex:1, minHeight:0, display:"flex", flexDirection:"column", overflowY:"auto" }}>
      <div style={{ padding:"16px 24px", display:"flex", flexDirection:"column", gap:16 }}>

        {/* COMPANY WIDGET — always visible */}
        {!companyDrilled ? (
          <>
            {/* Company widget full width */}
            <div onClick={()=>setCompanyDrilled(true)}
            style={{ background:"linear-gradient(135deg,#020d1a,#051e35)", border:"1px solid rgba(0,212,255,0.2)", borderRadius:20, padding:28, cursor:"pointer" }}
            onMouseEnter={e=>e.currentTarget.style.borderColor="#8b5cf6"}
            onMouseLeave={e=>e.currentTarget.style.borderColor="rgba(139,92,246,0.4)"}
          >
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:20 }}>
              <div style={{ fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.15em", color:"#00d4ff" }}>Better Debt Solutions</div>
              <div style={{ fontSize:22, fontWeight:900, color:"#e2e8f0" }}>Company Overview</div>
              <div style={{ width:160 }}/>{/* spacer to keep title centered */}
            </div>
            {/* Daily / Weekly / Monthly breakdown */}
            <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12, marginBottom:20 }}>
              {[
                {label:"Daily",   src:"DAILY",  s:stats(allTasks.filter(t=>t._source==="DAILY"))},
                {label:"Weekly",  src:"WEEKLY", s:stats(allTasks.filter(t=>t._source==="WEEKLY"))},
                {label:"Monthly", src:"MTD",    s:stats(allTasks.filter(t=>t._source==="MTD"))},
                {label:"Reviews", src:"REVIEWS",s:stats([...(data?.callReviewTasks||[]),...(data?.oneOnOneTasks||[])])},
              ].map(({label,src,s})=>(
                <div key={label} onClick={e=>{ e.stopPropagation(); setCompanyDrilled(true); setDrillTeam("ALL"); setDrillSource(src); setFilter("Open"); setSourceFilter("All"); }}
                  style={{ background:"rgba(0,0,0,0.25)", borderRadius:14, padding:"18px 20px", cursor:"pointer", transition:"background 0.15s" }}
                  onMouseEnter={e=>e.currentTarget.style.background="rgba(0,212,255,0.09)"}
                  onMouseLeave={e=>e.currentTarget.style.background="rgba(0,0,0,0.25)"}
                >
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14 }}>
                    <div style={{ fontSize:13, fontWeight:700, color:"#94a3b8", textTransform:"uppercase", letterSpacing:"0.08em" }}>{label}</div>
                    <div style={{ fontSize:15, fontWeight:900, color:pctColor(s.pct) }}><AnimatedNumber key={src+s.pct} value={s.pct} color={pctColor(s.pct)} fontSize={15}/>%</div>
                  </div>
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:8, marginBottom:12 }}>
                    {[{l:"Open",v:s.open,c:"#f87171"},{l:"Pending",v:s.pending,c:"#00d4ff"},{l:"Done",v:s.done,c:"#34d399"}].map(x=>(
                      <div key={x.l} style={{ textAlign:"center" }}>
                        <AnimatedNumber key={src+x.l+x.v} value={x.v} color={x.c} fontSize={26}/>
                        <div style={{ fontSize:10, color:"#475569", marginTop:3 }}>{x.l}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ background:"rgba(0,0,0,0.3)", borderRadius:999, height:5, overflow:"hidden" }}>
                    <div style={{ width:`${Math.min(s.pct,100)}%`, height:"100%", borderRadius:999, background:pctColor(s.pct), transition:"width 0.6s ease" }}/>
                  </div>
                </div>
              ))}
            </div>


            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:6, marginTop:12 }}>
              <span style={{ fontSize:12, color:"#64748b" }}>Overall · {companyStats.done}/{companyStats.total} tasks</span>
              <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                {companyStats.pending > 0 && (
                  <button onClick={e=>{ e.stopPropagation(); setCompanyDrilled(true); setDrillTeam("ALL"); setDrillSource("ALL"); setFilter("Pending Review"); setSourceFilter("All"); }}
                    style={{ background:"rgba(0,212,255,0.1)", border:"1px solid rgba(0,212,255,0.3)", borderRadius:8, padding:"4px 14px", color:"#00d4ff", fontSize:12, fontWeight:700, cursor:"pointer" }}>
                    View {companyStats.pending} Pending →
                  </button>
                )}
                <span style={{ fontSize:14, fontWeight:700, color:pctColor(companyStats.pct) }}><AnimatedNumber key={companyStats.pct} value={companyStats.pct} color={pctColor(companyStats.pct)} fontSize={14}/>%</span>
              </div>
            </div>
            <div style={{ background:"rgba(0,0,0,0.3)", borderRadius:999, height:8, overflow:"hidden" }}>
              <div style={{
                width:`${companyStats.pct}%`, height:"100%", borderRadius:999, transition:"width 0.4s ease",
                background: companyStats.pct === 0 ? "#ef4444" :
                  companyStats.pct <= 25 ? `hsl(${companyStats.pct*1.2},85%,55%)` :
                  companyStats.pct <= 50 ? `hsl(${30+(companyStats.pct-25)*1.2},85%,55%)` :
                  companyStats.pct <= 75 ? `hsl(${60+(companyStats.pct-50)*1.6},85%,50%)` :
                  `hsl(${100+(companyStats.pct-75)*0.8},75%,45%)`
              }}/>
            </div>
        </div>
            {/* Top row: Goals Tracker left, Chat right */}
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, alignItems:"stretch" }}>
              <div style={{ minHeight:240, display:"flex", flexDirection:"column" }}>
                <YesterdayStatsUploader onSaved={loadData}/>
              </div>
              <div style={{ minHeight:240, display:"flex", flexDirection:"column" }}>
                <BroadcastFeed broadcast={data?.broadcast||[]} userName={userName} userEmail={userEmail} isDirector={true} onReply={()=>{}} narrow={true}/>
              </div>
            </div>
            {/* Bottom row: Manual Task full width */}
            <PushTaskWidget teamMaster={data?.teamMaster||[]} onPushed={()=>{}}/>
          </>
        ) : (
          <div onClick={()=>setCompanyDrilled(false)} style={{ background:"linear-gradient(135deg,#020d1a,#051e35)", border:"1px solid rgba(0,212,255,0.15)", borderRadius:12, padding:"12px 20px", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
            <div style={{ display:"flex", alignItems:"center", gap:16 }}>
              <span style={{ fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.12em", color:"#00d4ff" }}>Better Debt Solutions</span>
              <div style={{ display:"flex", gap:12 }}>
                {[{label:"Open",value:companyStats.open,color:"#f87171"},{label:"Pending",value:companyStats.pending,color:"#00d4ff"},{label:"Done",value:companyStats.done,color:"#34d399"}].map(s=>(
                  <span key={s.label} style={{ fontSize:13, fontWeight:700, color:s.color }}>{s.value} <span style={{ color:"#475569", fontWeight:400 }}>{s.label}</span></span>
                ))}
              </div>
              <span style={{ fontSize:13, fontWeight:700, color:pctColor(companyStats.pct) }}>{companyStats.pct}%</span>
            </div>
            <span style={{ fontSize:11, color:"#64748b" }}>← collapse</span>
          </div>
        )}

        {/* DRILLED IN — show teams */}
        {companyDrilled && (
          <div style={{ display:"flex", alignItems:"center", gap:12 }}>
            <button onClick={()=>{ setCompanyDrilled(false); setDrillTeam(null); setDrillSource(null); }}
              style={{ background:"none", border:"1px solid #2a3148", borderRadius:8, padding:"6px 12px", color:"#64748b", fontSize:13, cursor:"pointer" }}>← Company</button>
            <span style={{ fontSize:13, color:"#64748b" }}>All Teams</span>
          </div>
        )}



        {/* ALL TASKS pending approve button */}
        {companyDrilled && !resolvedDrillTeam && allTasks.filter(t=>t.Task_Status==="Pending Review").length > 0 && (
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", background:"#1a1030", border:"1px solid rgba(139,92,246,0.3)", borderRadius:10, padding:"10px 16px" }}>
            <span style={{ fontSize:13, color:"#a78bfa", fontWeight:600 }}>
              {allTasks.filter(t=>t.Task_Status==="Pending Review").length} tasks pending review across all teams
            </span>
            <button onClick={()=>{ setDrillTeam("ALL"); setDrillSource("ALL"); setFilter("Pending Review"); }}
              style={{ background:"#8b5cf6", color:"#fff", border:"none", borderRadius:8, padding:"6px 16px", fontWeight:700, fontSize:13, cursor:"pointer" }}>
              View All Pending →
            </button>
          </div>
        )}


        {companyDrilled && !resolvedDrillTeam && (
          <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
            {/* All Teams card */}
            <div style={{ background:"#1e2840", border:"2px solid rgba(0,212,255,0.3)", borderRadius:16, padding:"14px 20px", display:"flex", alignItems:"center", justifyContent:"space-between", cursor:"pointer" }}
              onClick={()=>{ setDrillTeam("ALL"); setDrillSource("ALL"); setFilter("Open"); }}
              onMouseEnter={e=>e.currentTarget.style.borderColor="#00d4ff"}
              onMouseLeave={e=>e.currentTarget.style.borderColor="rgba(0,212,255,0.3)"}
            >
              <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                <span style={{ fontSize:14, fontWeight:700, color:"#00d4ff" }}>🌐 All Teams</span>
                <span style={{ fontSize:12, color:"#475569" }}>— Pipeline view across all teams</span>
              </div>
              <div style={{ display:"flex", gap:16 }}>
                {[
                  {label:"Open",    value:allTasks.filter(t=>t.Task_Status==="Open").length,           color:"#e2e8f0"},
                  {label:"Pending", value:allTasks.filter(t=>t.Task_Status==="Pending Review").length, color:"#00d4ff"},
                  {label:"Done",    value:allTasks.filter(t=>t.Task_Status==="Completed").length,      color:"#34d399"},
                ].map(s=>(
                  <div key={s.label} style={{ textAlign:"center" }}>
                    <div style={{ fontSize:18, fontWeight:900, color:s.color }}>{s.value}</div>
                    <div style={{ fontSize:10, color:"#475569" }}>{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
            {teamStats.map((s,i) => (
              <div key={s.team} style={{ background:"#1e2840", border:"1px solid #2a3148", borderRadius:16, padding:"16px 20px" }}>
                <div style={{ fontSize:13, fontWeight:700, color:"#e2e8f0", marginBottom:12, letterSpacing:"0.05em" }}>{s.team}</div>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12, marginBottom:12 }}>
                  {["DAILY","WEEKLY","MTD","ALL"].map(src => {
                    const srcStats = src==="DAILY"?s.daily:src==="WEEKLY"?s.weekly:src==="MTD"?s.mtd:s.total;
                    return (
                      <div key={src} onClick={()=>{ setDrillTeam(s.team); setDrillSource(src); setFilter("Pending Review"); }}
                        style={{ background:"#151c2e", border:"1px solid #2a3148", borderRadius:12, padding:"12px 14px", cursor:"pointer" }}
                        onMouseEnter={e=>e.currentTarget.style.borderColor="#8b5cf6"}
                        onMouseLeave={e=>e.currentTarget.style.borderColor="#2a3148"}
                      >
                        <div style={{ fontSize:10, fontWeight:700, color:"#64748b", textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:10 }}>
                          {src === "DAILY" ? "Daily" : src === "WEEKLY" ? "Weekly" : src === "MTD" ? "MTD" : "All"}
                        </div>
                        <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:6 }}>
                          {[{label:"Open",value:srcStats.open,color:"#f87171"},{label:"Pend",value:srcStats.pending,color:"#00d4ff"},{label:"Done",value:srcStats.done,color:"#34d399"}].map(st=>(
                            <div key={st.label} style={{ background:"#0f1520", borderRadius:6, padding:"6px 4px", textAlign:"center" }}>
                              <div style={{ fontSize:16, fontWeight:900, color:st.color }}>{st.value}</div>
                              <div style={{ fontSize:9, color:"#475569", marginTop:1 }}>{st.label}</div>
                            </div>
                          ))}
                        </div>
                        <div style={{ marginTop:8, fontSize:11, fontWeight:700, color:pctColor(srcStats.pct), textAlign:"right" }}>{srcStats.pct}%</div>
                        <div style={{ marginTop:6, background:"#0f1520", borderRadius:999, height:5, overflow:"hidden" }}>
                          <div style={{
                            width:`${srcStats.pct}%`,
                            height:"100%",
                            borderRadius:999,
                            background: srcStats.pct === 0 ? "#ef4444" :
                              srcStats.pct <= 25 ? `hsl(${0 + srcStats.pct * 1.2}, 85%, 55%)` :
                              srcStats.pct <= 50 ? `hsl(${30 + (srcStats.pct-25) * 1.2}, 85%, 55%)` :
                              srcStats.pct <= 75 ? `hsl(${60 + (srcStats.pct-50) * 1.6}, 85%, 50%)` :
                              `hsl(${100 + (srcStats.pct-75) * 0.8}, 75%, 45%)`,
                            transition:"width 0.4s ease"
                          }}/>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* DRILL LEVEL 1 — Team selected, pick source */}
        {resolvedDrillTeam && !drillSource && (
          <>
            <div style={{ display:"flex", alignItems:"center", gap:12 }}>
              <button onClick={()=>setDrillTeam(null)} style={{ background:"none", border:"1px solid #2a3148", borderRadius:8, padding:"6px 12px", color:"#64748b", fontSize:13, cursor:"pointer" }}>← Back</button>
              <span style={{ fontWeight:700, fontSize:16, color:"#e2e8f0" }}>{resolvedDrillTeam}</span>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:16 }}>
              {["DAILY","WEEKLY","MTD","ALL"].map(src => {
                const s = teamStats.find(t=>t.team===resolvedDrillTeam);
                const srcStats = src==="DAILY"?s?.daily:src==="WEEKLY"?s?.weekly:src==="MTD"?s?.mtd:s?.total;
                return (
                  <div key={src} onClick={()=>{ setDrillSource(src); setFilter("Pending Review"); }}
                    style={{ background:"#1e2840", border:"1px solid #2a3148", borderRadius:16, padding:20, cursor:"pointer" }}
                    onMouseEnter={e=>e.currentTarget.style.borderColor="#8b5cf6"}
                    onMouseLeave={e=>e.currentTarget.style.borderColor="#2a3148"}
                  >
                    <div style={{ fontWeight:700, fontSize:15, color:"#e2e8f0", marginBottom:12 }}>{src === "DAILY" ? "Daily" : src === "WEEKLY" ? "Weekly" : src === "MTD" ? "MTD" : "All"}</div>
                    <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:8, marginBottom:12 }}>
                      {[{label:"Open",value:srcStats?.open,color:"#f87171"},{label:"Pending",value:srcStats?.pending,color:"#a78bfa"},{label:"Done",value:srcStats?.done,color:"#34d399"}].map(st=>(
                        <div key={st.label} style={{ background:"#151c2e", borderRadius:8, padding:"8px", textAlign:"center" }}>
                          <div style={{ fontSize:20, fontWeight:900, color:st.color }}>{st.value}</div>
                          <div style={{ fontSize:10, color:"#475569", marginTop:2 }}>{st.label}</div>
                        </div>
                      ))}
                    </div>
                    <div style={{ fontWeight:700, color:pctColor(srcStats?.pct||0), fontSize:13 }}>{srcStats?.pct||0}% Complete</div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* DRILL LEVEL 2 — Team + Source selected, show tasks */}
        {resolvedDrillTeam && drillSource && (
          <>
            {/* Breadcrumb */}
            <div style={{ display:"flex", alignItems:"center", gap:12 }}>
              <button onClick={()=>{ if(resolvedDrillTeam==="ALL"){setDrillTeam(null);setDrillSource(null);}else{setDrillTeam(null);setDrillSource(null);} setSelected({}); }} style={{ background:"none", border:"1px solid #2a3148", borderRadius:8, padding:"6px 12px", color:"#64748b", fontSize:13, cursor:"pointer" }}>← Back</button>
              <span style={{ color:"#64748b", fontSize:13 }}>{resolvedDrillTeam === "ALL" ? "All Teams" : resolvedDrillTeam}</span>
              <span style={{ color:"#475569" }}>›</span>
              <span style={{ fontWeight:700, fontSize:15, color:"#e2e8f0" }}>{drillSource === "DAILY" ? "Daily" : drillSource === "WEEKLY" ? "Weekly" : drillSource === "MTD" ? "MTD" : "All"}</span>
            </div>

            {/* Source sub-filter — only when viewing ALL source */}
            {(drillSource === "ALL" || resolvedDrillTeam === "ALL") && (
              <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
                {["All","DAILY","WEEKLY","MTD","REVIEWS"].map(src=>(
                  <button key={src} onClick={()=>{ setSourceFilter(src); setSelected({}); }}
                    style={{ background:sourceFilter===src?"rgba(139,92,246,0.15)":"#1e2840", color:sourceFilter===src?"#a78bfa":"#64748b", border:`1px solid ${sourceFilter===src?"rgba(139,92,246,0.3)":"#2a3148"}`, borderRadius:8, padding:"4px 12px", fontSize:12, fontWeight:500, cursor:"pointer" }}>
                    {src === "DAILY" ? "Daily" : src === "WEEKLY" ? "Weekly" : src === "MTD" ? "Monthly" : src === "REVIEWS" ? "Reviews" : "All Sources"}
                  </button>
                ))}
              </div>
            )}

            {/* Status filter */}
            <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
              {["Open","Pending Review","Completed","All"].map(f=>(
                <button key={f} onClick={()=>{ setFilter(f); setSelected({}); }} style={{ background:filter===f?"rgba(0,212,255,0.1)":"#1e2840", color:filter===f?"#00d4ff":"#64748b", border:`1px solid ${filter===f?"rgba(0,212,255,0.3)":"#2a3148"}`, borderRadius:8, padding:"4px 12px", fontSize:12, fontWeight:500, cursor:"pointer" }}>
                  {f}{f==="Pending Review"&&pendingTasks.length>0&&<span style={{ marginLeft:6, background:"#00d4ff", color:"#000", borderRadius:9999, padding:"1px 5px", fontSize:10, fontWeight:700 }}>{pendingTasks.length}</span>}
                </button>
              ))}
              <span style={{ fontSize:12, color:"#475569", marginLeft:8 }}>{filtered.length} tasks</span>
            </div>

            {/* Bulk approve */}
            {filter === "Pending Review" && pendingTasks.length > 0 && (
              <div style={{ display:"flex", alignItems:"center", gap:12, background:"#1a1030", border:"1px solid rgba(139,92,246,0.3)", borderRadius:10, padding:"10px 16px" }}>
                <input type="checkbox" checked={allPendingSelected} onChange={toggleSelectAll} style={{ width:16, height:16, cursor:"pointer", accentColor:"#8b5cf6" }}/>
                <span style={{ fontSize:13, color:"#a78bfa", fontWeight:500 }}>
                  {selectedKeys.length > 0 ? `${selectedKeys.length} selected` : "Select all pending"}
                </span>
                {selectedKeys.length > 0 && (
                  <button onClick={handleBulkApprove} disabled={bulkApproving} style={{ marginLeft:"auto", background:"#22c55e", color:"#000", border:"none", borderRadius:8, padding:"6px 20px", fontWeight:700, fontSize:13, cursor:bulkApproving?"not-allowed":"pointer" }}>
                    {bulkApproving ? "Approving..." : `✓ Approve ${selectedKeys.length} Task${selectedKeys.length!==1?"s":""}`}
                  </button>
                )}
              </div>
            )}

            {/* Task table */}
            <div style={{ border:"1px solid #2a3148", borderRadius:12 }}>
              <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
                <thead>
                  <tr style={{ background:"#151c2e", borderBottom:"1px solid #2a3148" }}>
                    {filter==="Pending Review" && <th style={{ padding:"10px 16px", width:40 }}></th>}
                    {["Source","Manager","Team","Type","Agent","Priority","Status","Proof",""].map(h=>(
                      <th key={h} style={{ textAlign:"left", padding:"10px 16px", fontSize:11, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.05em", color:"#475569" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((t,i)=>{
                    const key = t._rowIndex+t._sheetName;
                    const isPendingRow = (localStatuses[key] || t.Task_Status) === "Pending Review";
                    return (
                      <DirectorTaskRow
                        key={key||i}
                        task={{...t, Task_Type: t._typeCol}}
                        typeCol="_typeCol"
                        sheetName={t._sheetName}
                        source={t._source}
                        userEmail={userEmail}
                        userName={userName}
                        showCheckbox={filter==="Pending Review" && isPendingRow}
                        checked={!!selected[key]}
                        onCheck={e => setSelected(prev => ({...prev, [key]: e.target.checked}))}
                        onUpdated={()=>{}}
                      />
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* Team Chat — full width when drilled into teams */}
        {companyDrilled && !resolvedDrillTeam && (
          <BroadcastFeed broadcast={data?.broadcast||[]} userName={userName} userEmail={userEmail} isDirector={true} onReply={()=>{}}/>
        )}

      </div>
    </div>
  );
}

// ─── PUSH TASK ALERT ─────────────────────────────────────────────────────────
function PushTaskAlert({ tasks, onOpen, onDismiss }) {
  if (!tasks || tasks.length === 0) return null;
  return (
    <div style={{
      position:"fixed", top:0, left:0, right:0, bottom:0,
      background:"rgba(0,0,0,0.7)", zIndex:9999,
      display:"flex", alignItems:"center", justifyContent:"center",
      animation:"fadeIn 0.2s ease"
    }}>
      <div style={{
        background:"#1a0808", border:"2px solid #ef4444",
        borderRadius:20, padding:"32px 40px", maxWidth:480, width:"90%",
        display:"flex", flexDirection:"column", alignItems:"center", gap:16,
        boxShadow:"0 0 60px rgba(239,68,68,0.4)",
        animation:"slideUp 0.3s ease"
      }}>
        <div style={{ fontSize:48 }}>🚨</div>
        <div style={{ textAlign:"center" }}>
          <div style={{ fontSize:18, fontWeight:900, color:"#f87171", marginBottom:6 }}>
            🚨 New Task Assigned 🚨
          </div>
          <div style={{ fontSize:14, color:"#fca5a5", fontWeight:600 }}>
            Please complete as soon as possible
          </div>
        </div>
        {tasks.map((t,i) => (
          <div key={i} style={{ background:"rgba(239,68,68,0.1)", border:"1px solid rgba(239,68,68,0.3)", borderRadius:10, padding:"10px 16px", width:"100%", textAlign:"center" }}>
            <div style={{ fontSize:13, fontWeight:700, color:"#fca5a5" }}>{t.Task_Type}</div>
            <div style={{ fontSize:12, color:"#94a3b8", marginTop:3 }}>{t.Required_Action}</div>
          </div>
        ))}
        <div style={{ display:"flex", gap:12, marginTop:4 }}>
          <button onClick={onOpen} style={{ background:"#ef4444", color:"#fff", border:"none", borderRadius:10, padding:"10px 24px", fontWeight:700, fontSize:14, cursor:"pointer" }}>
            Open Task →
          </button>
          <button onClick={onDismiss} style={{ background:"transparent", color:"#64748b", border:"1px solid #2a3148", borderRadius:10, padding:"10px 24px", fontWeight:600, fontSize:14, cursor:"pointer" }}>
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── MANUAL TASK ALERT ───────────────────────────────────────────────────────
function ManualTaskAlert({ tasks, onOpen, onDismiss }) {
  const [visible, setVisible] = useState(false);
  const [shown,   setShown]   = useState(false);

  useEffect(() => {
    if (!tasks || tasks.length === 0) return;
    if (!shown) {
      setVisible(true);
      setShown(true);
    }
  }, [tasks]);

  if (!visible || !tasks || tasks.length === 0) return null;

  const task = tasks[0];

  return (
    <div style={{
      position:"fixed", top:20, right:20, zIndex:9999,
      background:"#1a0808", border:"2px solid #ef4444",
      borderRadius:16, padding:"18px 20px", maxWidth:360,
      boxShadow:"0 8px 32px rgba(239,68,68,0.3)",
      animation:"slideIn 0.3s ease"
    }}>
      <style>{`
        @keyframes slideIn { from { transform:translateX(120%); opacity:0; } to { transform:translateX(0); opacity:1; } }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.6} }
      `}</style>
      <div style={{ display:"flex", alignItems:"flex-start", gap:12 }}>
        <span style={{ fontSize:24, animation:"pulse 1.5s infinite" }}>🚨</span>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:13, fontWeight:800, color:"#f87171", marginBottom:4, lineHeight:1.4 }}>
            New Task Assigned — Please complete as soon as possible 🚨
          </div>
          <div style={{ fontSize:12, color:"#94a3b8", marginBottom:12 }}>
            {task.Task_Type || "Task"}{task.Required_Action ? ` · ${task.Required_Action.slice(0,60)}${task.Required_Action.length>60?"...":""}` : ""}
          </div>
          <div style={{ display:"flex", gap:8 }}>
            <button onClick={()=>{ setVisible(false); onOpen && onOpen(task); }}
              style={{ background:"#ef4444", color:"#fff", border:"none", borderRadius:8, padding:"6px 16px", fontWeight:700, fontSize:12, cursor:"pointer" }}>
              Open Task →
            </button>
            <button onClick={()=>{ setVisible(false); onDismiss && onDismiss(); }}
              style={{ background:"none", border:"1px solid #3a1a1a", color:"#64748b", borderRadius:8, padding:"6px 12px", fontSize:12, cursor:"pointer" }}>
              Dismiss
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── MAIN APP ─────────────────────────────────────────────────────────────────
export default function App() {
  const [user, setUser] = useState(() => {
    try {
      // First check URL for email param (bookmarked link)
      const urlParams = new URLSearchParams(window.location.search);
      const urlEmail = urlParams.get("email");
      if (urlEmail) {
        const trimmed = urlEmail.trim().toLowerCase();
        return { email: trimmed, name: trimmed.split("@")[0], loginTime: Date.now() };
      }
      // Fall back to localStorage
      const saved = localStorage.getItem("roqos_user");
      if (!saved) return null;
      const parsed = JSON.parse(saved);
      const twelveHours = 12 * 60 * 60 * 1000;
      if (Date.now() - parsed.loginTime > twelveHours) { localStorage.removeItem("roqos_user"); return null; }
      return parsed;
    } catch { return null; }
  });
  const [data,       setData]       = useState(null);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState("");
  const [clock,      setClock]      = useState(new Date());
  const [pushAlert,  setPushAlert]  = useState([]);
  const seenTasksRef = useRef(new Set());
  useEffect(() => {
    const t = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const [activeTab,  setActiveTab]  = useState("home");
  const [showNotifs, setShowNotifs] = useState(false);
  const [expandTask, setExpandTask] = useState(null); // rowIndex to auto-expand
  const isDirector = DIRECTOR_EMAILS.includes(user?.email);
  const [activeView, setActiveView] = useState(() => {
    try {
      const urlEmail = new URLSearchParams(window.location.search).get("email");
      if (urlEmail && DIRECTOR_EMAILS.includes(urlEmail.trim().toLowerCase())) return "director";
      const saved = localStorage.getItem("roqos_user");
      if (!saved) return "manager";
      const u = JSON.parse(saved);
      return DIRECTOR_EMAILS.includes(u.email) ? "director" : "manager";
    } catch { return "manager"; }
  });

  const loadData = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const fresh = await fetchSheetData();
      setData(fresh);
      // Check for new manual push tasks assigned to this manager
      if (fresh?.managerTasks) {
        const managerRow = fresh.teamMaster?.find(r=>(r["Manager Email"]||"").toLowerCase()===user?.email?.toLowerCase());
        if (managerRow) {
          const KNOWN_TYPES = ["DONUT_CHAT_GROUP","ZERO_CONVERSION","LOW_CONVERSION","STREAK_2","STREAK_3","STREAK_4","STREAK_5","FREQ_2_OF_3","FREQ_3_OF_5","SATURDAY_SIGNUPS_REVIEW","MORNING_MESSAGE_PREP","AGENT_TIME_DETAIL","FOLLOWUP_CALLS","WEEKLY_DOUBLE_MISS","WEEKLY_LOW_PRODUCTION","WEEKLY_LOW_CONVERSION","MTD_TRIPLE_MISS","MTD_PRODUCTION_CONVERSION","MTD_PRODUCTION_CANCEL","MTD_CONVERSION_CANCEL","MTD_LOW_PRODUCTION","MTD_LOW_CONVERSION","MTD_HIGH_CANCEL","GOALS_EMAIL","CALL_REVIEW","ONE_ON_ONE_1","ONE_ON_ONE_2","DAILY_SPOT_CHECK","COMPLIANCE_CHECK_1","COMPLIANCE_CHECK_2","COMPLIANCE_CHECK_3","COMPLIANCE_CHECK_4"];
          const newPush = fresh.managerTasks.filter(t =>
            t.Manager === managerRow.Manager &&
            !KNOWN_TYPES.includes(t.Task_Type) &&
            t.Task_Status === "Open" &&
            !seenTasksRef.current.has(t._rowIndex)
          );
          newPush.forEach(t => seenTasksRef.current.add(t._rowIndex));
          if (newPush.length > 0 && seenTasksRef.current.size > newPush.length) {
            setPushAlert(newPush);
          }
        }
      }
    }
    catch(e) { console.error(e); setError("Failed to load data: " + e.message); }
    setLoading(false);
  }, []);

  const switchView = useCallback((view) => {
    setActiveView(view);
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (user && !data) { loadData(); }
  }, [user, data, loadData]);

  useEffect(() => {
    if (!user) return;
    const interval = setInterval(() => { loadData(); }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user, loadData]);

  const handleLogin = useCallback((userInfo) => {
    const u = { ...userInfo, loginTime: Date.now() };
    localStorage.setItem("roqos_user", JSON.stringify(u));
    // Update URL so this device can be bookmarked
    const url = new URL(window.location.href);
    url.searchParams.set("email", u.email);
    window.history.replaceState({}, "", url.toString());
    setUser(u);
    // Set the correct view based on who just logged in
    setActiveView(DIRECTOR_EMAILS.includes(u.email) ? "director" : "manager");
  }, []);

  const handleSignOut = () => {
    localStorage.removeItem("roqos_user");
    const url = new URL(window.location.href);
    url.searchParams.delete("email");
    window.history.replaceState({}, "", url.toString());
    setUser(null);
    setData(null);
    setActiveView("manager");
  };

  // Notifications — tasks with director notes the manager hasn't cleared
  const allManagerTasks = [
    ...(data?.managerTasks||[]),
    ...(data?.weeklyTasks||[]),
    ...(data?.mtdTasks||[]),
    ...(data?.prepTasks||[]),
  ];
  const notifTasks = !isDirector && user ? allManagerTasks.filter(t => {
    const notes = t.Completion_Notes || t.completion_notes || "";
    const mgr = (data?.teamMaster||[]).find(r=>(r["Manager Email"]||"").toLowerCase()===user.email.toLowerCase());
    const myName = mgr?.Manager || "";
    const isMyTask = t.Manager === myName;
    return isMyTask && notes.startsWith("[Director]:");
  }) : [];

  // Filter call review and 1-on-1 tasks out of daily, they show under monthly
  const MONTHLY_TASK_TYPES = ["CALL_REVIEW","ONE_ON_ONE_1","ONE_ON_ONE_2"];

  const filterByManager = (tasks, managerCol="Manager") => {
    if (!tasks||!user||!data) return [];
    if (isDirector) return tasks;
    const row = data.teamMaster.find(r=>(r["Manager Email"]||"").toLowerCase()===user.email.toLowerCase());
    if (!row) return [];
    return tasks.filter(t=>t[managerCol]===row["Manager"]);
  };

  if (!user) return <LoginScreen onLogin={handleLogin}/>;

  const isManualTaskType = (t) => !["DONUT_CHAT_GROUP","ZERO_CONVERSION","LOW_CONVERSION","STREAK_2","STREAK_3","STREAK_4","STREAK_5","FREQ_2_OF_3","FREQ_3_OF_5","SATURDAY_SIGNUPS_REVIEW","MORNING_MESSAGE_PREP","AGENT_TIME_DETAIL","FOLLOWUP_CALLS","WEEKLY_DOUBLE_MISS","WEEKLY_LOW_PRODUCTION","WEEKLY_LOW_CONVERSION","MTD_TRIPLE_MISS","MTD_PRODUCTION_CONVERSION","MTD_PRODUCTION_CANCEL","MTD_CONVERSION_CANCEL","MTD_LOW_PRODUCTION","MTD_LOW_CONVERSION","MTD_HIGH_CANCEL","GOALS_EMAIL","CALL_REVIEW","ONE_ON_ONE_1","ONE_ON_ONE_2","DAILY_SPOT_CHECK","COMPLIANCE_CHECK_1","COMPLIANCE_CHECK_2","COMPLIANCE_CHECK_3","COMPLIANCE_CHECK_4"].includes(t.Task_Type);

  if (loading && !data) return (
    <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:"#0f1520", fontFamily:"'Inter',system-ui,sans-serif" }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:16 }}>
        <div style={{ width:32, height:32, border:"2px solid #00d4ff", borderTopColor:"transparent", borderRadius:"50%", animation:"spin 0.8s linear infinite" }}/>
        <div style={{ color:"#64748b", fontSize:13 }}>Loading your tasks...</div>
      </div>
    </div>
  );

  const myTeamRow  = (data?.teamMaster||[]).find(r => (r["Manager Email"]||"").toLowerCase() === (user?.email||"").toLowerCase());
  const myTeamName = myTeamRow?.Team || "";

  const myPrep   = filterByManager(data?.prepTasks    ||[]);
  const myDaily  = filterByManager((data?.managerTasks||[]).filter(t => !MONTHLY_TASK_TYPES.includes(t.Task_Type)));
  const myManualTasks = myDaily.filter(t => t.Task_Status === "Open" && !["DONUT_CHAT_GROUP","ZERO_CONVERSION","LOW_CONVERSION","STREAK_2","STREAK_3","STREAK_4","STREAK_5","FREQ_2_OF_3","FREQ_3_OF_5","SATURDAY_SIGNUPS_REVIEW","MORNING_MESSAGE_PREP","AGENT_TIME_DETAIL","FOLLOWUP_CALLS","WEEKLY_DOUBLE_MISS","WEEKLY_LOW_PRODUCTION","WEEKLY_LOW_CONVERSION","MTD_TRIPLE_MISS","MTD_PRODUCTION_CONVERSION","MTD_PRODUCTION_CANCEL","MTD_CONVERSION_CANCEL","MTD_LOW_PRODUCTION","MTD_LOW_CONVERSION","MTD_HIGH_CANCEL","GOALS_EMAIL","CALL_REVIEW","ONE_ON_ONE_1","ONE_ON_ONE_2","DAILY_SPOT_CHECK","COMPLIANCE_CHECK_1","COMPLIANCE_CHECK_2","COMPLIANCE_CHECK_3","COMPLIANCE_CHECK_4"].includes(t.Task_Type));
  const myMonthlyFromDaily = filterByManager((data?.managerTasks||[]).filter(t => MONTHLY_TASK_TYPES.includes(t.Task_Type))).map(t=>({...t, MTD_Flag: t.Task_Type, _sheetName:"MANAGER_TASKS"}));
  const myWeekly = filterByManager(data?.weeklyTasks  ||[]);
  const myCRTasks = filterByManager(data?.callReviewTasks||[], "Manager");
  const myOOTasks = filterByManager(data?.oneOnOneTasks||[], "Manager");
  const myReviews = [...myCRTasks, ...myOOTasks];
  const myMtd    = filterByManager(data?.mtdTasks||[]);

  const openCount = myDaily.filter(t=>t.Task_Status==="Open").length;
  const critCount = myDaily.filter(t=>t.Priority==="Critical"&&t.Task_Status==="Open").length;
  const pendCount = myDaily.filter(t=>t.Task_Status==="Pending Review").length;
  const doneCount = myDaily.filter(t=>t.Task_Status==="Completed").length;

  const tabs = [
    {id:"home",    label:"🏠 Home",           count:0},
    {id:"prep",    label:"Morning Prep",     count:myPrep.filter(t=>t.Task_Status==="Open").length},
    {id:"daily",   label:"Daily Tasks",      count:myDaily.filter(t=>t.Task_Status==="Open").length},
    {id:"weekly",  label:"Weekly Tasks",     count:myWeekly.filter(t=>t.Task_Status==="Open").length},
    {id:"mtd",     label:"Monthly Tasks",     count:myMtd.filter(t=>t.Task_Status==="Open").length},
    {id:"reviews", label:"📞 Call Reviews", count:0},
  ];

  return (
    <div style={{ height:"100vh", display:"flex", flexDirection:"column", overflow:"hidden", background:"#0f1520", fontFamily:"'Inter',system-ui,sans-serif", color:"#e2e8f0" }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}} textarea:focus{outline:none;border-color:#3a4160!important;} @keyframes fadeIn{from{opacity:0}to{opacity:1}} @keyframes slideUp{from{transform:translateY(20px);opacity:0}to{transform:translateY(0);opacity:1}}`}</style>
      {!isDirector && pushAlert.length > 0 && (
        <PushTaskAlert
          tasks={pushAlert}
          onOpen={()=>{ setPushAlert([]); setActiveTab("daily"); }}
          onDismiss={()=>setPushAlert([])}
        />
      )}

      {/* NAV */}
      <nav style={{ flexShrink:0, height:56, display:"flex", alignItems:"center", justifyContent:"space-between", padding:"0 24px", background:"#1a1f2e", borderBottom:"1px solid #2a3148" }}>
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <span style={{ fontWeight:900, fontSize:18, letterSpacing:"0.3em" }}>RO<span style={{ color:"#00d4ff" }}>QOS</span></span>
          <div style={{ width:1, height:16, background:"#2a3148" }}/>
          <span style={{ fontSize:11, textTransform:"uppercase", letterSpacing:"0.15em", color:"#64748b" }}>
            {isDirector&&activeView==="director"?"Director View":"Manager Portal"}
          </span>
          <div style={{ width:1, height:16, background:"#2a3148" }}/>
          <span style={{ fontSize:12, color:"#00d4ff", fontWeight:600, letterSpacing:"0.05em" }}>
            {clock.toLocaleDateString("en-US",{weekday:"short",month:"short",day:"numeric"})} · {clock.toLocaleTimeString("en-US",{hour:"numeric",minute:"2-digit",second:"2-digit",hour12:true})}
          </span>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <button onClick={loadData} style={{ background:"none", border:"none", cursor:"pointer", padding:6, borderRadius:8, color:"#64748b" }} title="Refresh">
            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/></svg>
          </button>
          {!isDirector && notifTasks.length > 0 && (
            <button onClick={()=>setShowNotifs(v=>!v)} style={{ position:"relative", background:"none", border:"none", cursor:"pointer", padding:6, borderRadius:8, color:"#64748b" }} title="Notifications">
              <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"/></svg>
              <span style={{ position:"absolute", top:2, right:2, background:"#f87171", color:"#fff", borderRadius:9999, fontSize:9, fontWeight:900, minWidth:14, height:14, display:"flex", alignItems:"center", justifyContent:"center", padding:"0 3px" }}>{notifTasks.length}</span>
            </button>
          )}
          {isDirector && (
            <div style={{ display:"flex", gap:4, background:"#151c2e", border:"1px solid #2a3148", borderRadius:8, padding:4 }}>
              <button onClick={()=>switchView("director")} style={{ background:activeView==="director"?"#8b5cf6":"transparent", color:activeView==="director"?"#fff":"#64748b", border:"none", borderRadius:6, padding:"4px 12px", fontSize:12, fontWeight:700, cursor:"pointer" }}>
                Director{pendCount>0&&<span style={{ marginLeft:4, background:"#8b5cf6", color:"#fff", borderRadius:9999, padding:"1px 5px", fontSize:10 }}>{pendCount}</span>}
              </button>
              <button onClick={()=>switchView("manager")} style={{ background:activeView==="manager"?"#00d4ff":"transparent", color:activeView==="manager"?"#000":"#64748b", border:"none", borderRadius:6, padding:"4px 12px", fontSize:12, fontWeight:700, cursor:"pointer" }}>Manager</button>
            </div>
          )}
          <div style={{ display:"flex", alignItems:"center", gap:8, background:"#151c2e", border:"1px solid #2a3148", borderRadius:8, padding:"6px 12px" }}>
            <div style={{ width:24, height:24, borderRadius:"50%", background:"rgba(0,212,255,0.15)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, fontWeight:700, color:"#00d4ff" }}>{user.name?.[0]}</div>
            <span style={{ fontSize:14, fontWeight:500 }}>{user.name?.split(" ")[0]}</span>
            <button onClick={handleSignOut} style={{ background:"none", border:"none", cursor:"pointer", fontSize:12, color:"#64748b", marginLeft:4 }}>Sign out</button>
          </div>
        </div>
      </nav>

      {error && <div style={{ flexShrink:0, padding:"8px 24px", background:"#1a0808", borderBottom:"1px solid #3a1a1a", color:"#f87171", fontSize:13 }}>{error} <button onClick={loadData} style={{ background:"none", border:"none", color:"#f87171", textDecoration:"underline", cursor:"pointer", marginLeft:8 }}>Retry</button></div>}

      {/* Notification panel */}
      {showNotifs && notifTasks.length > 0 && (
        <div style={{ flexShrink:0, background:"#1a1030", borderBottom:"1px solid rgba(139,92,246,0.3)", padding:"12px 24px", display:"flex", flexDirection:"column", gap:8 }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
            <span style={{ fontSize:13, fontWeight:700, color:"#a78bfa" }}>🔔 Director Notes ({notifTasks.length})</span>
            <button onClick={()=>setShowNotifs(false)} style={{ background:"none", border:"none", color:"#64748b", cursor:"pointer", fontSize:12 }}>✕ Close</button>
          </div>
          {notifTasks.map((t,i) => (
            <div key={i} style={{ background:"#1e2840", borderRadius:8, padding:"10px 14px", display:"flex", flexDirection:"column", gap:4, cursor:"pointer" }} onClick={()=>{ setShowNotifs(false); setExpandTask(t._rowIndex); setActiveTab(t.Weekly_Flag?"weekly":t.MTD_Flag?"mtd":"daily"); }}>
              <div style={{ fontSize:12, fontWeight:600, color:"#e2e8f0" }}>{t.Agent_Name||t.Agents_List||t.Task_Type} — {t.Task_Type}</div>
              <div style={{ fontSize:12, color:"#a78bfa" }}>{(t.Completion_Notes||"").replace("[Director]:","Director:").substring(0,120)}</div>
              <div style={{ fontSize:11, color:"#475569" }}>Click to open task tab</div>
            </div>
          ))}
        </div>
      )}

      {isDirector&&activeView==="director"?<div style={{ flex:1, minHeight:0, display:"flex", flexDirection:"column" }}><DirectorView data={data} userEmail={user.email} userName={user.name} loadData={loadData}/></div>:(
        <div style={{ flex:1, minHeight:0, display:"flex", flexDirection:"column", overflow:"hidden" }}>
          <div style={{ flexShrink:0, display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:16, padding:"12px 24px", borderBottom:"1px solid #2a3148" }}>
            {[
              {label:"Open Tasks",     value:openCount,  color:"#e2e8f0"},
              {label:"Critical",       value:critCount,  color:"#f87171"},
              {label:"Pending Review", value:pendCount,  color:"#a78bfa"},
              {label:"Completed",      value:doneCount,  color:"#34d399"},
              {label:"Call Reviews",   value:myCRTasks.filter(t=>t.Task_Status==="Completed").length + "/" + myCRTasks.length, color:"#00d4ff", isText:true},
            ].map(s=>(
              <div key={s.label} onClick={s.label==="Call Reviews"?()=>setActiveTab("reviews"):undefined}
                style={{ background:"#1e2840", border:"1px solid #2a3148", borderRadius:12, padding:"10px 16px", cursor:s.label==="Call Reviews"?"pointer":"default" }}>
                {s.isText
                  ? <div style={{ fontSize:22, fontWeight:700, color:s.color, lineHeight:1.2 }}>{s.value}</div>
                  : <AnimatedNumber key={s.label+s.value} value={s.value} color={s.color} fontSize={22}/>
                }
                <div style={{ fontSize:12, color:"#64748b", marginTop:2 }}>{s.label}</div>
              </div>
            ))}
          </div>
          <div style={{ flexShrink:0, display:"flex", gap:4, padding:"12px 24px 0", borderBottom:"1px solid #2a3148" }}>
            {tabs.map(tab=>(
              <button key={tab.id} onClick={()=>setActiveTab(tab.id)} style={{ display:"flex", alignItems:"center", gap:8, padding:"8px 16px", borderRadius:"8px 8px 0 0", fontSize:13, fontWeight:500, cursor:"pointer", background:activeTab===tab.id?"#1e2840":"transparent", color:activeTab===tab.id?"#e2e8f0":"#64748b", border:"none", borderBottom:activeTab===tab.id?"2px solid #00d4ff":"2px solid transparent" }}>
                {tab.label}
                {tab.count>0&&<span style={{ fontSize:11, fontWeight:700, padding:"1px 6px", borderRadius:9999, background:activeTab===tab.id?"rgba(0,212,255,0.15)":"rgba(255,255,255,0.06)", color:activeTab===tab.id?"#00d4ff":"#64748b" }}>{tab.count}</span>}
              </button>
            ))}
          </div>
          <div style={{ flex:1, minHeight:0, overflow:"hidden", display:"flex", flexDirection:"column" }}>
            {activeTab==="home" && (
              <div style={{ display:"flex", flexDirection:"column", gap:10, padding:"12px 24px", boxSizing:"border-box", overflowY:"auto" }}>
                {/* Task summary - compact */}
                <div style={{ flexShrink:0, display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10 }}>
                  {[
                    { label:"Daily", open:myDaily.filter(t=>t.Task_Status==="Open").length, pending:myDaily.filter(t=>t.Task_Status==="Pending Review").length, done:myDaily.filter(t=>t.Task_Status==="Completed").length },
                    { label:"Weekly", open:myWeekly.filter(t=>t.Task_Status==="Open").length, pending:myWeekly.filter(t=>t.Task_Status==="Pending Review").length, done:myWeekly.filter(t=>t.Task_Status==="Completed").length },
                    { label:"MTD", open:myMtd.filter(t=>t.Task_Status==="Open").length, pending:myMtd.filter(t=>t.Task_Status==="Pending Review").length, done:myMtd.filter(t=>t.Task_Status==="Completed").length },
                    { label:"Call Reviews", open:myCRTasks.filter(t=>t.Task_Status==="Open").length, pending:myCRTasks.filter(t=>t.Task_Status==="Pending Review").length, done:myCRTasks.filter(t=>t.Task_Status==="Completed").length, onClick:()=>setActiveTab("reviews") },
                  ].map(s => (
                    <div key={s.label} onClick={s.onClick} style={{ background:"#1e2840", border:"1px solid #2a3148", borderRadius:12, padding:"10px 14px", cursor:s.onClick?"pointer":"default" }}>
                      <div style={{ fontSize:11, fontWeight:700, color:"#94a3b8", marginBottom:8, textTransform:"uppercase", letterSpacing:"0.1em" }}>{s.label}{s.label!=="Call Reviews"?" Tasks":""}</div>
                      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:6 }}>
                        {[{label:"Open",value:s.open,color:"#f87171"},{label:"Pending",value:s.pending,color:"#a78bfa"},{label:"Done",value:s.done,color:"#34d399"}].map(stat=>(
                          <div key={stat.label} style={{ background:"#151c2e", borderRadius:6, padding:"6px 4px", textAlign:"center" }}>
                            <div style={{ fontSize:18, fontWeight:900, color:stat.color, lineHeight:1.1 }}>{stat.value}</div>
                            <div style={{ fontSize:9, color:"#475569", marginTop:1 }}>{stat.label}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                {/* Bottom row: Deal Tracker + Team Chat - fills remaining space */}
                <div style={{ display:"grid", gridTemplateColumns:"1.4fr 1fr", gap:10, alignItems:"start" }}>
                  <div>
                    <DealTracker managerEmail={user.email} teamName={myTeamName}/>
                  </div>
                  <div style={{ minHeight:0, display:"flex", flexDirection:"column", overflow:"hidden" }}>
                    <BroadcastFeed broadcast={data?.broadcast||[]} userName={user.name} userEmail={user.email} isDirector={false} onReply={loadData}/>
                  </div>
                </div>
              </div>
            )}
            {activeTab!=="home" && (
              <div style={{ flex:1, overflowY:"auto", padding:"16px 24px" }}>
            {activeTab==="prep"   && <PrepView tasks={myPrep} userEmail={user.email} userName={user.name} isDirector={isDirector}/>}
            {activeTab==="daily"  && <TaskTable tasks={myDaily}  typeCol="Task_Type"   sheetName="MANAGER_TASKS"        userEmail={user.email} userName={user.name} isDirector={isDirector} expandTask={expandTask} onExpanded={()=>setExpandTask(null)}/>}
            {activeTab==="weekly" && <TaskTable tasks={myWeekly} typeCol="Weekly_Flag" sheetName="WEEKLY_MANAGER_TASKS" userEmail={user.email} userName={user.name} isDirector={isDirector} expandTask={expandTask} onExpanded={()=>setExpandTask(null)}/>}
            {activeTab==="mtd"    && <TaskTable tasks={myMtd}    typeCol="MTD_Flag"    sheetName="MTD_MANAGER_TASKS"    userEmail={user.email} userName={user.name} isDirector={isDirector} expandTask={expandTask} onExpanded={()=>setExpandTask(null)}/>}
            {activeTab==="reviews" && <ReviewsTable tasks={myReviews} userEmail={user.email} userName={user.name} isDirector={isDirector} onRefresh={loadData}/>}
            {activeTab==="reviews" && <CallReviewsArchive callReviews={data?.callReviews||[]} user={user} isDirector={isDirector} teamMaster={data?.teamMaster||[]}/>}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
